/* configurator.js — pure option/price/geometry logic for the omlijst sliding-door configurator.
   Ported from design-reference/ui_kits/product-configurator/configurator.jsx.
   ESM export for Node tests; also sets window.cfgLogic for browser use.
   Task 4: <door-configurator> custom element (options column + gallery placeholder).
   Task 5: 2D layered gallery — FrontScene, ProfileScene, GalleryViewer. */

const CFG = {
  MIN_PER_DOOR: 600, MAX_PER_DOOR: 1000, COUNTS: [2, 3, 4, 5],
  GEOM: { 2: { x: 32.0, x2: 68.0 }, 3: { x: 23.07, x2: 76.93 }, 4: { x: 14.4, x2: 85.6 }, 5: { x: 5.6, x2: 94.27 } },
  DOOR_TOP: 23.2, DOOR_BOT: 78.0,
};

const OMLIJST_WIDTH_RANGE = { 2: [1180, 1980], 3: [1760, 2960], 4: [2360, 3960], 5: [2940, 4940] };
const PANEEL_WIDTH_RANGE  = { 2: [960, 1960],  3: [1420, 2910], 4: [1880, 3870], 5: [2340, 4830] };

export const cfgLogic = {
  OMLIJST_WIDTH_RANGE,
  PANEEL_WIDTH_RANGE,
  validCounts(w, ranges = OMLIJST_WIDTH_RANGE) { return Object.keys(ranges).map(Number).filter((n) => w >= ranges[n][0] && w <= ranges[n][1]); },
  countRange(n, ranges = OMLIJST_WIDTH_RANGE) { return ranges[n] || null; },
  widthMin(ranges = OMLIJST_WIDTH_RANGE) { return Math.min(...Object.values(ranges).map((r) => r[0])); },
  widthMax(ranges = OMLIJST_WIDTH_RANGE) { return Math.max(...Object.values(ranges).map((r) => r[1])); },
  nearestCount(w, cur, ranges = OMLIJST_WIDTH_RANGE) {
    const v = cfgLogic.validCounts(w, ranges);
    if (!v.length || v.includes(cur)) return v.includes(cur) ? cur : (v[0] ?? cur);
    return v.reduce((a, b) => Math.abs(b - cur) < Math.abs(a - cur) ? b : a);
  },
  slots(count, inset) {
    const g = CFG.GEOM[count], w = (g.x2 - g.x) / count, out = [];
    for (let i = 0; i < count; i++) out.push({ left: g.x + w * i + inset, width: w - inset * 2, top: CFG.DOOR_TOP, height: CFG.DOOR_BOT - CFG.DOOR_TOP });
    return out;
  },
  lum(hex) { const n = parseInt(hex.slice(1), 16); return (0.2126 * (n >> 16 & 255) + 0.7152 * (n >> 8 & 255) + 0.0722 * (n & 255)) / 255; },
  priceLines(sel, p) {
    /* variant.price is the total for this specific (count × frame) variant, e.g. 2-deurs = 2 × per-deur.
       Rows are per-decor breakdown: "decor" (non-mirror doors, or all doors when spiegelActive),
       "spiegel" (mirror doors at FULL spiegel-per-door price, not a delta), "softclose", "montage". */
    const count       = sel.count;
    const mirrorCount = sel.mirrors.length;
    const decorCount  = count - mirrorCount;
    const doorsC      = (p.variantPriceCents || 0);
    const spiegelDoorC = p.spiegelDoorCents || 0;
    /* base per-door price:
       - spiegelActive (mirror baked into variant): back out mirror cost, split remainder over decor count
       - base decor (no bake-in): variant price divided by full count */
    const basePerDoorC = p.spiegelActive
      ? (decorCount > 0 ? Math.round((doorsC - mirrorCount * spiegelDoorC) / decorCount) : 0)
      : (count > 0 ? Math.round(doorsC / count) : 0);
    const decorC      = decorCount * basePerDoorC;
    const spiegelC    = mirrorCount * spiegelDoorC;
    const softC       = (sel.soft ? count : 0) * (p.softCents || 0);
    const montageC    = (sel.montage ? 1 : 0) * (p.montageCents || 0);
    /* mirror toeslag added on top ONLY when mirror is NOT baked into the variant */
    const mirrorToeslagC = p.spiegelActive ? 0 : mirrorCount * (p.mirrorCents || 0);
    const total       = doorsC + mirrorToeslagC + softC + montageC;
    const compareC    = (p.variantCompareCents && p.variantCompareCents > doorsC)
      ? (p.variantCompareCents + mirrorToeslagC + softC + montageC) : null;
    const lines = [];
    if (decorCount > 0) {
      lines.push({ key: "decor", label: p.decorLabel || `${decorCount} deuren`, count: decorCount, amount: decorC, compareAt: null });
    }
    if (mirrorCount > 0) {
      lines.push({ key: "spiegel", label: "Spiegel deuren", count: mirrorCount, amount: spiegelC, compareAt: null });
    }
    if (softC > 0) {
      lines.push({ key: "softclose", label: "Soft-close", count, amount: softC, compareAt: null });
    }
    if (montageC > 0) {
      lines.push({ key: "montage", label: "Montageservice", count: 1, amount: montageC, compareAt: null });
    }
    return { total, compareAt: compareC, lines };
  },
  matchVariant(variants, { countLabel, frameLabel, mirrorCount }) {
    const countKey = mirrorCount && mirrorCount > 0 ? `${countLabel} met ${mirrorCount} spiegel` : countLabel;
    return variants.find((v) => v.options.includes(countKey) && v.options.includes(frameLabel)) || null;
  },
  /* Assemble the /cart/add.js payload from already-resolved pieces. Pure (no DOM,
     no fetch) so the cart money-paths are unit-testable:
       - parent door line carries the visible props + _bundle_id + _cfg
       - soft-close is a bundled child (one per door)
       - montage is a STANDALONE order-level line (_max1, cart-only _note)
       - mirror child is paneel-only (omlijst bakes mirror cost into the variant,
         so callers pass mirrorId=0 there)
     Line order matches the legacy builder: [parent, soft-close, montage, mirror]. */
  buildCartPayload({ sel, variantId, parentProps, cfgString, bundleId, softId, montageId, mirrorId, montageNote }) {
    /* Line order: bundled add-ons (soft-close, mirror) are added BEFORE the parent
       door line on purpose. Shopify's checkout order summary lists the lines in
       reverse of cart order, so adding the door last makes it appear ABOVE its
       add-ons there. The theme cart page + drawer are property-based
       (_bundle_id / Breedte), so they stay door-first regardless of this order. */
    const items = [];
    if (sel.soft && softId) {
      items.push({ id: softId, quantity: sel.count, properties: { _bundle_id: bundleId } });
    }
    if (mirrorId && sel.mirrors.length) {
      items.push({ id: mirrorId, quantity: sel.mirrors.length, properties: { _bundle_id: bundleId } });
    }
    items.push({
      id: variantId,
      quantity: 1,
      properties: { ...parentProps, _bundle_id: bundleId, _cfg: cfgString },
    });
    if (sel.montage && montageId) {
      items.push({ id: montageId, quantity: 1, properties: { _max1: "1", _note: montageNote } });
    }
    return { items };
  },
  euro(cents) { return "€ " + (cents / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
  priceGroup(variantPriceCents, doorCount, thresholdCents = 88500) {
    const perDoor = variantPriceCents / Math.max(1, doorCount);
    return perDoor <= thresholdCents ? "wit" : "overig";
  },

  /* ── parametric door geometry (matches slidingDoorFrame.js) ── */
  panelWidthFromTotal(totalWmm, count, F) {
    return (totalWmm - 2 * F.sideFrameW - (count - 1) * F.mullionW) / count;
  },
  panelOpenings(count, panelWmm, doorHmm, F) {
    const doorWmm = 2 * F.sideFrameW + count * panelWmm + (count - 1) * F.mullionW;
    const topAssemblyH = F.topCapH + F.topGapH + F.topChannelH;
    const innerTopY = topAssemblyH;
    const innerH = doorHmm - topAssemblyH - F.botTrackH;
    const out = [];
    for (let i = 0; i < count; i++) {
      const xmm = F.sideFrameW + i * (panelWmm + F.mullionW);
      out.push({
        xPct: xmm / doorWmm * 100,
        wPct: panelWmm / doorWmm * 100,
        yPct: innerTopY / doorHmm * 100,
        hPct: innerH / doorHmm * 100,
      });
    }
    return out;
  },
  materialCrop(panelWmm, panelHmm, maxPanelWmm, maxPanelHmm) {
    return {
      w: maxPanelWmm / panelWmm * 100,
      h: maxPanelHmm / panelHmm * 100,
    };
  },
  frameColorKey(frameLabel) {
    return { Zwart: "black", Wit: "white", Aluminium: "aluminum", Kwartsgrijs: "kwartsgrijs", Antraciet: "antraciet" }[frameLabel] || "black";
  },
};

if (typeof window !== "undefined") window.cfgLogic = cfgLogic;

/* =============================================================
   TASK 4 — <door-configurator> custom element
   Controls column: panel swatches, dimensions, door count,
   frame swatches, mirror picker, soft-close switch, price block.
   Gallery column: placeholder <div class="cfg-gallery"> for Task 5.
   ============================================================= */

/* ── Scene constants (ported from configurator.jsx) ──────────────────── */

/** Floor Y position (% of square image). Skirting sits just above it. */
const CFG_FLOOR_Y = 78.7;

/** Wall colours offered as swatches over the front gallery view — a range from
 *  bright neutrals through warm mids to darker, moodier tones. */
const CFG_WALL_COLORS = [
  "#F4F1EA", // warm white (bright)
  "#E3E6E2", // soft cool grey (default)
  "#DCD4C6", // greige
  "#CBB7A1", // clay / tan
  "#9FA992", // sage green
  "#6E7B74", // muted slate-green
  "#445057", // deep charcoal slate
  "#2E3A4A", // deep navy
];

/** Floor backgrounds keyed by floorStyle. */
const CFG_FLOOR_BG = {
  "Licht hout": "linear-gradient(180deg, rgba(255,255,255,0.30), rgba(29,29,27,0.07)), repeating-linear-gradient(90deg, #D9C7AB 0, #D9C7AB 7.2%, #D2BFA2 7.2%, #D2BFA2 7.4%, #DECDB2 7.4%, #DECDB2 14.6%, #D4C2A6 14.6%, #D4C2A6 14.8%)",
  "Beton": "linear-gradient(180deg, #D5D3CE, #C3C1BC)",
};

/** Profile bar gradients per frame id (lowercase). */
const CFG_PROFILE_BAR = {
  Zwart:       "linear-gradient(90deg, #181816, #363631 42%, #232321 75%, #141412)",
  Wit:         "linear-gradient(90deg, #E6E5E1, #FBFAF7 35%, #EDECE8 70%, #DCDBD6)",
  Aluminium:   "linear-gradient(90deg, #8C9499, #DADEE0 30%, #A7AFB3 55%, #E5E8E9 78%, #959DA1)",
  Kwartsgrijs: "linear-gradient(90deg, #706B62, #A19B90 35%, #75706A 70%, #605B54)",
  Antraciet:   "linear-gradient(90deg, #40444A, #6B7076 40%, #454A50 75%, #33373B)",
};

const CFG_COUNTS_EL = [2, 3, 4, 5];
const CFG_MIN_PER_DOOR_EL = 600;
const CFG_MAX_PER_DOOR_EL = 1000;

/* Fixed mm → % of the square stage for the "no zoom" (constant camera) mode:
   max width 5000mm ≈ 91%, max height 2850mm ≈ 52%. */
const CFG_MM_TO_PCT = 0.0182;

/* Per-door material flips so adjacent doors don't look identical (real wood varies).
   door 1 = normal, 2 = mirror vertical, 3 = mirror horizontal, 4 = 180°, then repeat. */
const CFG_PANEL_FLIPS = ["", "scaleY(-1)", "scaleX(-1)", "scaleX(-1) scaleY(-1)"];

/** Escape for safe HTML insertion. */
function _esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Sanitize a free-text CSS value before interpolation into style="…".
 *  Strips characters that can break out of an HTML attribute string. */
function _sanitizeCss(s) {
  return String(s).replace(/["<>]/g, "");
}

/** Render an SVG checkmark (used for USP row). */
function _checkSvg() {
  return '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--mint-700)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
}

/* ── Guard: only define the custom element in a browser environment ────── */
if (typeof HTMLElement !== "undefined" && typeof customElements !== "undefined") {

class DoorConfigurator extends HTMLElement {
  /* ── lifecycle ─────────────────────────────────────── */
  connectedCallback() {
    const scriptEl = this.querySelector("script[data-cfg]");
    if (!scriptEl) { console.error("<door-configurator>: missing <script data-cfg>"); return; }
    try { this._island = JSON.parse(scriptEl.textContent); }
    catch (e) { console.error("<door-configurator>: JSON parse error", e); return; }

    const d = this._island.defaults;
    this.sel = {
      panel:   d.panel   ?? "olijfgroen",
      w:       d.w       ?? 2400,
      h:       d.h       ?? 2600,
      count:   d.count   ?? 3,
      frame:   d.frame   ?? "Zwart",
      mirrors: d.mirrors ? [...d.mirrors] : [],
      soft:    d.soft    ?? false,
      montage: d.montage ?? false,
      wall:    d.wall    ?? CFG_WALL_COLORS[1],
    };
    /* paneel-only: seed kleur + vlak from island.active (omlijst leaves these unset) */
    if (this._island.type === "paneel") {
      const active = this._island.active || {};
      this.sel.kleur = active.kleur ?? (this._island.kleuren && this._island.kleuren[0] && this._island.kleuren[0].id) ?? null;
      this.sel.vlak  = active.vlak  ?? (this._island.vlakverdelingen && this._island.vlakverdelingen[0] && this._island.vlakverdelingen[0].id) ?? null;
    }
    /* Omlijst full-spiegel auto-switch target (not a swatch — resolved on demand
       when all doors are marked as mirror). null on paneel or when unset. */
    this._spiegelAll = this._island.spiegel_all || null;
    this._prevPanelBeforeSpiegel = null;
    /* Direct landing on a spiegel-decor product (defaults.panel is composite):
       seed the base decor so _maybeSwitchSpiegel resolves the real decor instead
       of panels[0]. Runs before _applyDeepLink so an "Aanpassen" link still wins. */
    if (typeof this.sel.panel === "string" && this.sel.panel.startsWith("spiegel-decor:")) {
      this._prevPanelBeforeSpiegel = this.sel.panel.slice("spiegel-decor:".length);
    }
    /* Pre-fill from a "Aanpassen" deep-link (?cfg=1&w=&h=&count=&frame=&mirrors=&soft=) */
    this._applyDeepLink();
    /* If a deep-link (or in-page landing on the spiegel product) has all doors
       mirrored, seed the spiegel-alles state so title/price/URL reflect it. */
    this._maybeSwitchSpiegel({ skipUrl: true });
    this._notice = null;

    /* Judge.me preview badge node (rendered + populated outside this element).
       Moved into the header on each render so its content survives re-renders. */
    this._reviewNode = document.querySelector(".cfg-jdgm-source .jdgm-preview-badge");

    /* bind hover handlers once so they can be removed/re-added cleanly */
    this._onHelpHover = this._handleHelpHover.bind(this);

    this._render();
    /* _bindEvents() is called inside _render() */

    /* Safety net: if the frame module hasn't loaded yet (script order race),
       re-render once it appears so the profile isn't left blank. */
    if (typeof window !== "undefined" && !window.SlidingDoorFrame) {
      let tries = 0;
      const waitForFrame = () => {
        if (window.SlidingDoorFrame) { this._render(); return; }
        if (tries++ < 60) requestAnimationFrame(waitForFrame);
      };
      requestAnimationFrame(waitForFrame);
    }
  }

  /** Restore selection from an "Aanpassen" deep-link written by the cart pencil.
   *  Panel is implicit (it's this product) so it is not read here. */
  _applyDeepLink() {
    if (typeof window === "undefined" || !window.location || !window.location.search) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("cfg") !== "1" && !params.get("bundle")) return;

    /* Resolve this door-type's width ranges + height bounds the same way _render
       does, so deep-link values get clamped to real, orderable configurations
       instead of trusting whatever a hand-edited or truncated URL supplies. */
    const dt = ((typeof window !== "undefined" && window.CfgDoorTypes) || {})[this._island.type || "omlijst"] || {};
    const ranges = dt.ranges || cfgLogic.OMLIJST_WIDTH_RANGE;
    const hMin = dt.heightMin || 1800;
    const hMax = dt.heightMax || 2850;
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

    const intOr = (key, fallback) => {
      const v = parseInt(params.get(key), 10);
      return Number.isFinite(v) ? v : fallback;
    };
    /* width first (clamped to the type's range), then snap the door count to a
       value that is actually valid at that width. */
    if (params.get("w") != null) this.sel.w = clamp(intOr("w", this.sel.w), cfgLogic.widthMin(ranges), cfgLogic.widthMax(ranges));
    if (params.get("h") != null) this.sel.h = clamp(intOr("h", this.sel.h), hMin, hMax);
    if (params.get("count") != null) this.sel.count = cfgLogic.nearestCount(this.sel.w, intOr("count", this.sel.count), ranges);

    const frame = params.get("frame");
    if (frame && this._island.frames && this._island.frames.some((f) => f.id === frame)) this.sel.frame = frame;

    /* mirrors: in-range indices only, deduped + sorted — a fuzzed "1,1,1" must not
       masquerade as "all doors mirrored". */
    const mirrors = params.get("mirrors");
    this.sel.mirrors = mirrors
      ? [...new Set(mirrors.split(",").map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n) && n >= 0 && n < this.sel.count))].sort((a, b) => a - b)
      : [];

    this.sel.soft = params.get("soft") === "1";
    this.sel.montage = params.get("montage") === "1";
    {
      /* wall reaches the front-scene background unescaped — accept only a hex colour. */
      const wall = params.get("wall");
      if (wall && /^#[0-9a-fA-F]{3,8}$/.test(wall)) this.sel.wall = wall;
    }
    {
      /* Restore the sel.panel value from the deep-link. Accepts:
         - plain base decor id (matches an island.panels entry)
         - "spiegel-alles" or "spiegel-alles:<baseId>" (only with injected _spiegelAll)
         - "spiegel-decor:<baseId>" (only when baseId has a linked spiegel_product) */
      const panel = params.get("panel");
      const panels = this._island.panels || [];
      if (panel) {
        if (panels.some((p) => p.id === panel)) {
          this.sel.panel = panel;
        } else if (panel === "spiegel-alles" && this._spiegelAll) {
          this.sel.panel = "spiegel-alles";
        } else if (panel.startsWith("spiegel-alles:") && this._spiegelAll) {
          /* spiegel-alles carries its underlying decor so unchecking a mirror
             reverts to that decor, not panels[0] (Wit). */
          const baseId = panel.slice("spiegel-alles:".length);
          if (panels.some((p) => p.id === baseId)) this._prevPanelBeforeSpiegel = baseId;
          this.sel.panel = "spiegel-alles";
        } else if (panel.startsWith("spiegel-decor:")) {
          const baseId = panel.slice("spiegel-decor:".length);
          const base = panels.find((p) => p.id === baseId);
          if (base && base.spiegel_product) {
            this.sel.panel = panel;
            /* Track the base panel so uncheck-all-mirrors reverts here, not to panels[0]. */
            this._prevPanelBeforeSpiegel = baseId;
          }
        }
      }
    }
    /* "Aanpassen": remember which bundle we're editing. The old cart lines are
       cleared only AFTER a successful re-add (see _addToCart), so abandoning the
       edit (back button, closed tab) leaves the original order untouched. */
    const bundle = params.get("bundle");
    if (bundle) this._replaceBundleId = bundle;
    /* paneel-only: restore kleur + vlak from deep-link (only when valid in island) */
    if (this._island.type === "paneel") {
      const kleur = params.get("kleur");
      if (kleur && this._island.kleuren && this._island.kleuren.some((k) => k.id === kleur)) {
        this.sel.kleur = kleur;
      }
      const vlak = params.get("vlak");
      if (vlak && this._island.vlakverdelingen && this._island.vlakverdelingen.some((v) => v.id === vlak)) {
        this.sel.vlak = vlak;
      }
    }
  }

  /** Serialise the current selection as a deep-link query string (?cfg=1&…),
   *  shared by colour-switch navigation and the cart "Aanpassen" link. */
  _cfgQueryString() {
    /* spiegel-alles carries its underlying decor (spiegel-alles:<baseId>) so the
       "Aanpassen" roundtrip can revert to the right decor instead of panels[0]. */
    let panelParam = this.sel.panel || "";
    if (panelParam === "spiegel-alles" && this._prevPanelBeforeSpiegel) {
      panelParam = "spiegel-alles:" + this._prevPanelBeforeSpiegel;
    }
    const params = {
      cfg:     "1",
      w:       this.sel.w,
      h:       this.sel.h,
      count:   this.sel.count,
      frame:   this.sel.frame,
      mirrors: this.sel.mirrors.join(","),
      soft:    this.sel.soft ? "1" : "0",
      montage: this.sel.montage ? "1" : "0",
      wall:    this.sel.wall || "",
      panel:   panelParam,
    };
    /* paneel-only: persist kleur + vlak in the deep-link */
    if (this._island.type === "paneel") {
      params.kleur = this.sel.kleur || "";
      params.vlak  = this.sel.vlak  || "";
    }
    /* renderer hints (used by cart-thumbnail redraw + admin diagnostics).
       Ignored on hydration — cfg-configurator only reads the sel.* keys.
       Use _visualPanel() so spiegel-decor / spiegel-alles states resolve to
       the underlying base decor's hex, not panels[0] (which would fall back
       to Wit for any non-plain sel.panel value). */
    params.type = this._island.type || "omlijst";
    const _actKleur = this._activeKleur && this._activeKleur();
    const _actPanel = this._island && this._island.panels ? this._visualPanel() : null;
    const _hex = (_actKleur && _actKleur.hex) || (_actPanel && _actPanel.hex) || "";
    if (_hex) params.hex = _hex;
    const _material = (_actPanel && _actPanel.material_url) || "";
    if (_material) params.material = _material;
    return new URLSearchParams(params).toString();
  }

  /** Resolve the Spiegel-toeslag variant for the current decor's price group.
   *  Each decor (panel) carries a `group` (e.g. "835"/"905"); the toeslag product
   *  has one variant per group. Falls back to the single mirror add-on. */
  _mirrorVariant() {
    const addons = this._island.addons || {};
    /* Use the visual (base) panel so spiegel-decor:<id> / spiegel-alles states
       resolve to the real decor's price group, not panels[0] (which would show a
       835-group "+€140" hint on a 905-group decor). */
    const panel  = this._visualPanel() || this._island.panels[0];
    const group  = panel && panel.group;
    const byGroup = addons.mirror_by_group;
    if (byGroup && group != null && byGroup[group]) {
      const v = byGroup[group];
      return { id: v.id, price: v.price || 0, compare: v.compare || 0 };
    }
    return { id: addons.mirror, price: addons.mirror_price || 0, compare: addons.mirror_compare || 0 };
  }

  /* ── paneel: resolve the matrix entry for the current kleur+vlak selection ── */
  _resolveProduct() {
    const island = this._island;
    const sel    = this.sel;
    return (island.matrix && island.matrix[sel.kleur + "|" + sel.vlak]) || null;
  }

  /* ── active decor (each colour swatch is its own product) ──
     Every panel carries its own title / variants / photos so switching colour
     re-renders locally (no reload). Add-to-cart resolves the SELECTED decor's
     variant. Fall back to the section's own product where a decor lacks data. */
  _activePanel() {
    /* Full-spiegel virtual "panel": pulls title, variants, images, product_url
       from the spiegel-schuifdeuren product injected by the section. */
    if (this.sel.panel === "spiegel-alles" && this._spiegelAll) return this._spiegelAll;
    /* Partial-mirror virtual "panel": product-level fields come from the base
       decor's spiegel_product (variant list, title, url), but visual fields
       (hex, material_url, label, images) fall back to the base panel so 2D
       preview and swatches still render in the base decor's colour. */
    if (typeof this.sel.panel === "string" && this.sel.panel.startsWith("spiegel-decor:")) {
      const baseId = this.sel.panel.slice("spiegel-decor:".length);
      const basePanel = this._island.panels.find((p) => p.id === baseId);
      if (!basePanel) return null;
      const sp = basePanel.spiegel_product;
      if (!sp) return basePanel;
      return {
        ...basePanel,
        title:       sp.title       || basePanel.title,
        product_url: sp.product_url || basePanel.product_url,
        variants:    sp.variants    || basePanel.variants,
      };
    }
    return this._island.panels.find((p) => p.id === this.sel.panel) || this._island.panels[0] || null;
  }
  /* Always the base decor panel — used for 2D render (hex, material, images).
     Independent of spiegel-decor / spiegel-alles state. */
  _visualPanel() {
    const sel = this.sel;
    let baseId = sel.panel;
    if (typeof baseId === "string" && baseId.startsWith("spiegel-decor:")) {
      baseId = baseId.slice("spiegel-decor:".length);
    } else if (baseId === "spiegel-alles") {
      baseId = this._prevPanelBeforeSpiegel;
    }
    const panels = this._island.panels || [];
    return panels.find((p) => p.id === baseId) || panels[0] || null;
  }
  _activeVariants() {
    if (this._island.type === "paneel") return this._resolveProduct()?.variants || [];
    const p = this._activePanel();
    if (p && p.variants && p.variants.length) return p.variants;
    /* The active decor (the current product page) always falls back to the page's
       own variants as a safety net. A non-active decor with no linked product is
       not yet orderable as itself — return none so add-to-cart shows unavailable
       instead of silently ordering the current product under another decor's name. */
    if (p && p.active) return this._island.variants || [];
    return [];
  }
  _activeTitle() {
    if (this._island.type === "paneel") return this._resolveProduct()?.title || this._island.productTitle || "";
    const p = this._activePanel();
    return (p && p.title) || this._island.productTitle || (p && p.label) || "";
  }
  _activeImages() {
    if (this._island.type === "paneel") return this._resolveProduct()?.images || [];
    const p = this._activePanel();
    return (p && Array.isArray(p.images)) ? p.images : [];
  }
  _activeKleur() {
    if (this._island.type !== "paneel") return null;
    const island = this._island;
    return (island.kleuren && island.kleuren.find((k) => k.id === this.sel.kleur)) || (island.kleuren && island.kleuren[0]) || null;
  }

  /* ── main render ───────────────────────────────────── */
  _render() {
    const island = this._island;
    const sel    = this.sel;

    /* Door-type plugin: resolve the module for island.type (default "omlijst")
       once, before any range/height/render use. `this._ranges` and `this._type`
       drive width counts, height bounds, the scene renderer, and the option
       schema below — so a second door-type only differs in its module data. */
    const cfgTypes = (typeof window !== "undefined" && window.CfgDoorTypes) || {};
    this._type   = cfgTypes[island.type || "omlijst"] || cfgTypes.omlijst || {
      id: "omlijst", ranges: cfgLogic.OMLIJST_WIDTH_RANGE, heightMin: 1800, heightMax: 2850,
      optionGroups: ["paneelkleur", "maten", "aantal", "profielkleur", "spiegel", "softclose", "montage"],
      renderDoor: (opts) => (typeof window !== "undefined" && window.SlidingDoorFrame)
        ? window.SlidingDoorFrame.renderDoor(opts) : "",
    };
    this._ranges = (this._type && this._type.ranges) || cfgLogic.OMLIJST_WIDTH_RANGE;

    const ui     = island.ui || {};
    const swatchRound  = ui.swatchRound  !== false;
    const swatchLabels = ui.swatchLabels !== false;

    /* preserve gallery view index (front=0 / profile=1) across re-renders */
    if (this._view === undefined) this._view = 0;

    /* variant + pricing — from the SELECTED decor (price differs per decor).
       spiegel-alles: variant title has no mirror-count suffix ("N-deurs / Frame") —
       the product itself IS the full-mirror door. spiegel-decor: variant title is
       "N-deurs met X spiegel / Frame" — pass mirrorCount so matchVariant finds it. */
    const inSpiegelAlles = sel.panel === "spiegel-alles";
    const inSpiegelDecor = typeof sel.panel === "string" && sel.panel.startsWith("spiegel-decor:");
    const matched = cfgLogic.matchVariant(this._activeVariants(), {
      countLabel: sel.count + "-deurs",
      frameLabel: sel.frame,
      mirrorCount: inSpiegelDecor ? sel.mirrors.length : 0,
    });
    /* I2: use live price from island; no hardcoded fallback — treat missing as 0 */
    /* mirror surcharge depends on the selected decor's price group (e.g. 835 → +€140, 905 → +€70) */
    /* mirror add-on cents — paneel uses mirror_by_group resolved at add-to-cart time;
       for the price display in _render we use the "wit" tier as a representative amount */
    let mirrorCents   = 0;
    let mirrorCompare = 0;
    if (island.type === "paneel") {
      const byGroup = island.addons?.mirror_by_group || {};
      const witM = byGroup["wit"];
      mirrorCents   = witM ? (witM.price   || 0) : 0;
      mirrorCompare = witM ? (witM.compare  || 0) : 0;
    } else {
      const mirrorVar = this._mirrorVariant();
      mirrorCents     = mirrorVar.price;
      mirrorCompare   = mirrorVar.compare;
    }
    const softCents      = island.addons?.softclose_price  ?? 0;
    const montageCents   = island.addons?.montage_price    ?? 0;
    const softCompare    = island.addons?.softclose_compare ?? 0;
    const montageCompare = island.addons?.montage_compare  ?? 0;
    const variantPrice   = matched ? matched.price       : 0;
    const compareAt      = (matched && matched.compare_at) ? matched.compare_at : 0;
    /* decorLabel / spiegelActive drive the per-decor price-overview rows (priceLines).
       Paneel has no "spiegel-alles" concept and no `island.panels`, so it derives its
       decor label from the active kleur swatch instead of _activePanel(). */
    const decorPanel = island.type === "paneel"
      ? null
      : (inSpiegelAlles && this._spiegelAll)
        ? this._spiegelAll
        /* spiegel-decor: the switched-to product (spiegel_product) carries no .label —
           resolve the label from the underlying base decor instead, so the price-line
           still reads e.g. "Wit deuren" rather than "undefined deuren". */
        : inSpiegelDecor
          ? (island.panels || []).find((pp) => pp.id === sel.panel.slice("spiegel-decor:".length)) || (island.panels || [])[0]
          : (island.panels || []).find((pp) => pp.id === sel.panel) || (island.panels || [])[0];
    const decorLabel = island.type === "paneel"
      ? ((this._activeKleur()?.label || sel.kleur || "") + " deuren")
      : (decorPanel ? (decorPanel.label + " deuren") : (sel.count + " deuren"));
    /* spiegelActive: the matched variant's price is ALREADY the full total for this
       door mix (decor + baked-in mirror pricing) on both spiegel-alles (all doors
       mirrored) and spiegel-decor (partial mirror) — so priceLines must not add a
       separate mirror toeslag on top, or the total would double-count the mirror cost. */
    const spiegelActive    = island.type !== "paneel" && (inSpiegelAlles || inSpiegelDecor);
    const spiegelDoorCents = (island.addons && island.addons.spiegel_door_cents) || 97500;
    const pricing = cfgLogic.priceLines(sel, {
      variantPriceCents: variantPrice,
      variantCompareCents: compareAt,
      mirrorCents,
      mirrorCompareCents: mirrorCompare,
      softCents,
      softCompareCents: softCompare,
      montageCents,
      montageCompareCents: montageCompare,
      spiegelDoorCents,
      decorLabel,
      spiegelActive,
    });

    const activePanel = this._visualPanel();
    const activeFrame = island.frames ? (island.frames.find((f) => f.id === sel.frame) || island.frames[0]) : null;
    const validCounts = cfgLogic.validCounts(sel.w, this._ranges);

    /* breadcrumb label + heading (both follow the selected decor) */
    const panelName = island.type === "paneel"
      ? (this._activeKleur()?.label || sel.kleur || "")
      : (activePanel ? activePanel.label : sel.panel);
    const productTitle = this._activeTitle();

    /* I5: detect unavailable variant */
    const unavailable = !matched || !matched.available;

    /* sticky bar spacer height */
    const stickyBar  = ui.stickyBar !== false && ui.stickyBar;
    const spacerHtml = stickyBar
      ? `<div class="cfg-sticky-spacer" aria-hidden="true" style="height:6.4rem"></div>`
      : "";
    const stickyBarHtml = stickyBar
      ? this._renderStickyBar(pricing, panelName, sel, activePanel, unavailable)
      : "";

    /* Slimmed hero embed: real 2D preview + colour + door count + deep-link CTA.
       One source of truth — same element, reduced layout. */
    if (ui.embed) {
      this.innerHTML = this._renderEmbed({ pricing, sel, island, validCounts, swatchRound, swatchLabels });
      this._bindEvents();
      this._applyLayoutTransition();
      return;
    }

    this.innerHTML = `
<div class="cfg-layout">
  <!-- GALLERY COLUMN -->
  <div class="cfg-gallery-col">
    <div class="cfg-gallery">
      ${this._renderGalleryViewer()}
    </div>
  </div>

  <!-- OPTIONS COLUMN -->
  <div class="cfg-options-col">
    <!-- rating + heading -->
    <div class="cfg-badges">
      <span class="cfg-rating-slot"></span>
    </div>
    <h1 class="cfg-title">${_esc(productTitle || panelName)}</h1>

    ${(() => {
      /* Step numbers are computed dynamically from the active door type's
         optionGroups (visible groups only). `vlakverdeling` is treated as a
         stylistic pre-step (no number); `montage` is folded into the "Opties"
         (softclose) group and produces no group of its own. */
      let _step = 0;
      const _ctx = {
        island, sel, panelName, activeFrame, validCounts, swatchRound, swatchLabels,
        mirrorCents, softCents, softCompare, montageCents, montageCompare,
      };
      return this._type.optionGroups.map((gid) => {
        const numbered = gid !== 'vlakverdeling' && gid !== 'montage';
        if (numbered) _step += 1;
        return this._renderOptionGroup(gid, _ctx, numbered ? _step : 0);
      }).filter(Boolean).join("\n\n    ");
    })()}

    <!-- Price block -->
    ${this._renderPriceBlock(pricing, ui, unavailable)}
  </div>
</div>
${spacerHtml}
${stickyBarHtml}`;

    this._bindEvents();
    this._mountReviewBadge();
    this._applyLayoutTransition();
  }

  /** Render one schema-driven control group by id. The caller passes a stepNum
   *  computed dynamically from the active door type's optionGroups (visible
   *  groups only). 0 means "unnumbered" (used for vlakverdeling and montage).
   *  "softclose" and "montage" share one visual "Opties" group — "softclose"
   *  renders the whole group (both bodies) and "montage" renders nothing. */
  _renderOptionGroup(id, ctx, stepNum) {
    const {
      island, sel, panelName, activeFrame, validCounts, swatchRound, swatchLabels,
      mirrorCents, softCents, softCompare, montageCents, montageCompare,
    } = ctx;
    switch (id) {
      case "vlakverdeling": {
        /* paneel-only stylistic pre-step (unnumbered: stepNum=0 from caller). */
        const activeVlak = island.vlakverdelingen && island.vlakverdelingen.find((v) => v.id === sel.vlak);
        const vlakLabel  = activeVlak ? activeVlak.label : (sel.vlak || "");
        return `<!-- Vlakverdeling -->\n    ` + this._renderGroup(stepNum, "Vlakverdeling", vlakLabel,
          "Kies de verdeling van de vlakken op het paneel.",
          this._renderVlakSwatches());
      }
      case "paneelkleur":
        if (island.type === "paneel") {
          const activeKleur = this._activeKleur();
          const kleurLabel  = activeKleur ? activeKleur.label : (sel.kleur || "");
          return `<!-- Paneelkleur -->\n    ` + this._renderGroup(stepNum, "Paneelkleur", kleurLabel,
            "Kies de kleur van het paneel.",
            this._renderKleurSwatches());
        }
        return `<!-- Paneelkleur -->\n    ` + this._renderGroup(stepNum, "Paneelkleur", panelName,
          "Elke kleur is een eigen productpagina — wisselen gaat zonder herladen en je instellingen reizen mee.",
          this._renderPanelSwatches(island.panels, sel, swatchRound, swatchLabels));
      case "maten":
        return `<!-- Maten -->\n    ` + this._renderGroup(stepNum, "Maten", "in mm",
          "Vul de totale breedte en hoogte van je opening in millimeters in. Breedte 1200–5000 mm, hoogte tot 2850 mm.",
          this._renderDimensions(sel, island));
      case "aantal":
        return `<!-- Aantal deuren -->\n    ` + this._renderGroup(stepNum, "Aantal deuren", null,
          "Het aantal mogelijke deuren hangt af van je breedte: reken 600–1000 mm per deur.",
          this._renderDoorCount(sel, validCounts, swatchRound, swatchLabels) +
          (this._notice ? `<p class="cfg-notice">${_esc(this._notice)}</p>` : ""));
      case "profielkleur":
        return `<!-- Profielkleur -->\n    ` + this._renderGroup(stepNum, "Profielkleur", activeFrame ? activeFrame.label : sel.frame,
          "De kleur van het aluminium kader rondom en tussen de panelen.",
          this._renderFrameSwatches(island.frames, sel, swatchRound, swatchLabels));
      case "spiegel": {
        /* Only offer mirrors when this decor can actually price them: it has a
           linked spiegel_product (partial mirror) or a spiegel-alles product
           exists (full mirror). Without either, mirror cost would be shown but
           never billed — so hide the option entirely. */
        const spiegelBase = this._visualPanel();
        const spiegelSupported = (spiegelBase && spiegelBase.spiegel_product) || this._spiegelAll;
        if (!spiegelSupported) return "";
        return `<!-- Spiegel -->\n    ` + this._renderGroup(stepNum, "Spiegel", "kies per deur",
          "Voeg een spiegelpaneel toe op één of meer deuren. Prijs per deur wordt bij het totaal opgeteld.",
          this._renderMirrorPicker(sel, mirrorCents));
      }
      case "softclose":
        /* "Opties" group bundles soft-close + montage (montage case is a no-op). */
        return `<!-- Opties -->\n    ` + this._renderGroup(stepNum, "Opties", null,
          "Soft-close laat de deuren automatisch afremmen en zacht sluiten. Montageservice: wij plaatsen je schuifdeuren vakkundig bij je thuis.",
          this._renderSoftClose(sel, softCents, softCompare) + this._renderMontage(sel, montageCents, montageCompare));
      case "montage":
        return "";
      default:
        return "";
    }
  }

  /** Slimmed embed layout for the homepage hero: the real front-view 2D preview,
   *  colour swatches + door count, a compact price, and a CTA that deep-links into
   *  the full product configurator with the current selection (?cfg=1&…). */
  _renderEmbed({ pricing, sel, island, validCounts, swatchRound, swatchLabels }) {
    const ui = island.ui || {};
    /* Keep every door the SAME panel width: total opening = count × door width,
       so the camera simply widens (and zooms out) as you add doors. */
    const doorW = ui.embedDoorWidth || 800;
    this.sel.w = sel.count * doorW;
    const front  = this._renderFrontScene(false);
    let panels = this._renderPanelSwatches(island.panels, sel, swatchRound, swatchLabels);
    /* optional trailing "+N / Bekijk alle kleuren" swatch that links out */
    const more = ui.embedMore;
    if (more && more.link) {
      const moreHtml = `<div class="cfg-swatch-wrap${swatchLabels ? " cfg-swatch-wrap--labeled" : ""}">
  <a class="cfg-swatch cfg-swatch--round cfg-embed-more" href="${_esc(more.link)}" aria-label="${_esc(more.label || "Bekijk alle kleuren")}" title="${_esc(more.label || "Bekijk alle kleuren")}"><span class="cfg-embed-more-badge">${_esc(more.badge || "+")}</span></a>
  ${swatchLabels ? `<span class="cfg-swatch-label">${_esc(more.label || "Bekijk alle kleuren")}</span>` : ""}
</div>`;
      panels = panels.replace(/<\/div>\s*$/, moreHtml + "</div>");
    }
    /* Dynamic, product-driven pricing: use the variants of the SELECTED colour's
       product (falling back to the section's configurator product). The door
       count picks the matching variant; counts the product doesn't offer are
       disabled. Price + "was" come straight from the variant (no manual fields). */
    const activeP = island.panels.find((p) => p.id === sel.panel) || island.panels[0];
    const variants = (activeP && activeP.variants && activeP.variants.length)
      ? activeP.variants
      : (island.variants || []);

    /* available door counts parsed from variant option values like "3-deurs" */
    let availCounts = [];
    variants.forEach((v) => (v.options || []).forEach((o) => {
      const m = /^(\d+)\s*-?\s*deurs?$/i.exec(String(o).trim());
      if (m) { const n = +m[1]; if (!availCounts.includes(n)) availCounts.push(n); }
    }));
    availCounts.sort((a, b) => a - b);
    if (!availCounts.length) availCounts = [2, 3, 4, 5];

    /* match the variant for the current count (+ frame when the product has one) */
    const cl = sel.count + "-deurs";
    const matched =
      variants.find((v) => (v.options || []).includes(cl) && (v.options || []).includes(sel.frame)) ||
      variants.find((v) => (v.options || []).some((o) => /^(\d+)\s*-?\s*deurs?$/i.test(String(o).trim()) && +String(o).match(/\d+/)[0] === sel.count)) ||
      variants[0] || null;
    const totalC   = matched ? matched.price : 0;
    const compareC = (matched && matched.compare_at) ? matched.compare_at : 0;
    const now = cfgLogic.euro(totalC);
    const was = (compareC > totalC) ? `<span class="cfg-embed-was">${cfgLogic.euro(compareC)}</span>` : "";

    /* offer only the counts this product has; others render disabled */
    const counts = this._renderDoorCount(sel, availCounts, swatchRound, false);
    const metaOverlay = ui.embedMeta ? `<span class="cfg-embed-meta-overlay">${_esc(ui.embedMeta)}</span>` : "";
    /* link the pills to the same destination as the CTA (deep-link, no hover) */
    const ctaBase = (activeP && activeP.product_url) || ui.embedFallbackUrl || "/";
    const ctaUrl = ctaBase + (ctaBase.indexOf("?") === -1 ? "?" : "&") + this._cfgQueryString();
    const further = (ui.embedFurther || "")
      .split(",").map((s) => s.trim()).filter(Boolean)
      .map((t) => `<a class="cfg-embed-tag" href="${_esc(ctaUrl)}">${_esc(t)}</a>`).join("");
    const furtherBlock = further
      ? `<div class="cfg-embed-further"><span class="cfg-embed-further-lbl">${_esc(ui.embedFurtherLabel || "Configureer verder:")}</span><div class="cfg-embed-tags">${further}</div></div>`
      : "";
    return `
<div class="cfg-embed-card cfg-embed-card--row">
  <div class="cfg-embed-aside">
    <div class="cfg-embed-field">
      <span class="cfg-embed-flabel">${_esc(ui.embedColorLabel || "Kleur")}</span>
      <div class="cfg-embed-swatches">${panels}</div>
    </div>
    <div class="cfg-embed-count cfg-embed-count--block">
      <span class="cfg-embed-flabel">${_esc(ui.embedDoorsLabel || "Deuren")}</span>
      ${counts}
    </div>
    ${furtherBlock}
    <div class="cfg-embed-price cfg-embed-price--block">
      <span class="cfg-embed-cap">${_esc(ui.embedPriceCaption || "Vanaf, incl. btw")}</span>
      <span class="cfg-embed-amounts">${was}<span class="cfg-embed-now">${now}</span></span>
    </div>
    <button class="cfg-btn-primary cfg-embed-cta" data-action="design-embed">
      ${_esc(ui.embedCtaLabel || "Ontwerp verder")}
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
    </button>
  </div>
  <div class="cfg-embed-view">
    <div class="cfg-embed-stage">${metaOverlay}${front}</div>
  </div>
</div>`;
  }

  /** Render sd-stars (same styling as the homepage reviews widget) above the
   *  product title, followed by the review count ("X reviews", formatted nl-NL).
   *  Reads both from the offscreen Judge.me preview-badge (which populates
   *  asynchronously) and re-paints when JM loads. Falls back to 5 full stars
   *  with no count until JM data arrives. */
  _mountReviewBadge() {
    if (!this._reviewNode) return;
    const slot = this.querySelector(".cfg-rating-slot");
    if (!slot) return;
    const STAR_PATH = "M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3.1-5.8 3.1 1.1-6.5L2.6 9.3l6.5-.9z";
    const renderStars = (rating) => {
      const r = Math.round(Math.max(0, Math.min(5, +rating || 5)));
      let svg = "";
      for (let i = 1; i <= 5; i++) {
        svg += `<svg viewBox="0 0 24 24" class="${i <= r ? "" : "sd-star--off"}" aria-hidden="true"><path d="${STAR_PATH}"></path></svg>`;
      }
      return `<span class="sd-stars" aria-label="${r} van de 5 sterren">${svg}</span>`;
    };
    const readBadge = () => {
      const badge = this._reviewNode.querySelector(".jdgm-prev-badge") || this._reviewNode;
      if (!badge || !badge.getAttribute) return { rating: null, count: null };
      const ra = badge.getAttribute("data-average-rating");
      const co = badge.getAttribute("data-number-of-reviews");
      return {
        rating: ra ? parseFloat(ra) : null,
        count:  co ? parseInt(co, 10) : null,
      };
    };
    const paint = () => {
      const { rating, count } = readBadge();
      /* Only show stars for products that actually have Judge.me reviews.
         Until JM populates (or if the product has zero reviews), render nothing
         so the title isn't preceded by placeholder 5-star row. */
      if (count == null || isNaN(count) || count <= 0) {
        slot.innerHTML = "";
        return;
      }
      const formatted = count.toLocaleString("nl-NL");
      slot.innerHTML = renderStars(rating == null ? 5 : rating) +
        ` <span class="cfg-rating-count">${formatted} review${count === 1 ? "" : "s"}</span>`;
    };
    /* Try a cached borrow synchronously — if a previous page already fetched
       a canonical group member's badge this session, apply it BEFORE first
       paint so stars appear on initial render (no flash of empty slot). */
    if (!this._jmBorrowTried) {
      this._jmBorrowTried = true;
      const cached = this._readBorrowCache();
      if (cached && cached.count > 0) {
        const mine = this._reviewNode.querySelector(".jdgm-prev-badge") || this._reviewNode;
        if (mine && (!mine.getAttribute("data-number-of-reviews") || parseInt(mine.getAttribute("data-number-of-reviews"), 10) <= 0)) {
          mine.setAttribute("data-average-rating", cached.avg);
          mine.setAttribute("data-number-of-reviews", String(cached.count));
        }
      }
    }
    paint();
    /* JM loads async; observe the offscreen source so we can re-paint when its
       data-average-rating attribute appears or changes. One observer per element
       instance — reuse it across re-renders. */
    if (!this._jmObserver && typeof MutationObserver !== "undefined") {
      this._jmObserver = new MutationObserver(paint);
      this._jmObserver.observe(this._reviewNode, { childList: true, subtree: true, attributes: true });
    }
    /* Fire the network borrow IMMEDIATELY (no delay) if we still show 0 —
       cache miss or stale. Section Rendering API fetch is ~8 KB, single
       round-trip, then we cache and paint. Subsequent same-session
       navigations hit the cache synchronously above. */
    if (!this._jmBorrowFetched) {
      this._jmBorrowFetched = true;
      const { count } = readBadge();
      if (count == null || count <= 0) this._borrowGroupBadge(paint);
    }
  }

  /** Fetch a canonical group-member's cfg-jdgm-source via the Section
   *  Rendering API (small response, ~8 KB) and lift its data-average-rating
   *  / data-number-of-reviews into ours. Result cached in sessionStorage so
   *  same-session navigations paint synchronously on first render.
   *  Silent on error — the badge just stays hidden. */
  _borrowGroupBadge(paint) {
    const island = this._island || {};
    let handle = null;
    const panels = island.panels || [];
    for (const p of panels) {
      if (p && p.product_url) {
        const m = p.product_url.match(/\/products\/([^/?#]+)/);
        if (m) { handle = m[1]; break; }
      }
    }
    if (!handle) handle = "beige-schuifdeuren";
    if (handle === (typeof location !== "undefined" ? location.pathname.split("/").pop() : "")) return;
    const url = "/products/" + handle + "?sections=main-product-configurator";
    fetch(url, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        const html = data && data["main-product-configurator"];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const src = doc.querySelector(".cfg-jdgm-source .jdgm-prev-badge");
        if (!src) return;
        const avg = src.getAttribute("data-average-rating");
        const cnt = src.getAttribute("data-number-of-reviews");
        const cntN = parseInt(cnt, 10);
        if (!cnt || isNaN(cntN) || cntN <= 0) return;
        this._writeBorrowCache({ avg, count: cntN });
        const mine = this._reviewNode.querySelector(".jdgm-prev-badge") || this._reviewNode;
        mine.setAttribute("data-average-rating", avg);
        mine.setAttribute("data-number-of-reviews", cnt);
        paint();
      })
      .catch(() => {});
  }

  _readBorrowCache() {
    try {
      const raw = sessionStorage.getItem("sd_jm_borrow_v1");
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (!p || typeof p.count !== "number") return null;
      return p;
    } catch (e) { return null; }
  }

  _writeBorrowCache(payload) {
    try { sessionStorage.setItem("sd_jm_borrow_v1", JSON.stringify(payload)); } catch (e) { /* quota / private mode */ }
  }

  /* ── gallery ───────────────────────────────────────── */

  /** Build the full gallery column content (viewer + thumbs). */
  _renderGalleryViewer() {
    const frontHtml   = this._renderFrontScene(false);
    const views = [
      { key: "front",   label: "Vooraanzicht", html: frontHtml },
    ];
    /* Profiel-detail view temporarily hidden — decor photos carry the profile shot for now */
    // if (this._island.type !== "paneel") {
    //   views.push({ key: "detail", label: "Profieldetail", html: this._renderProfileScene(false) });
    // }
    /* real product photos of the selected decor — shown after the 2 previews */
    this._activeImages().forEach((img, i) => {
      if (!img || !img.url) return;
      const alt = img.alt || `Productfoto ${i + 1}`;
      views.push({
        key:   "photo-" + i,
        label: alt,
        photo: true,
        html:  `<div class="cfg-scene cfg-scene--photo"><img class="cfg-photo" src="${_esc(img.url)}" alt="${_esc(alt)}" loading="lazy"></div>`,
      });
    });
    /* decors have different photo counts — keep the view index in range */
    let view = this._view ?? 0;
    if (view >= views.length || view < 0) view = 0;
    this._view = view;
    const current = views[view];

    const arrowPrev = `<button class="cfg-gallery-arrow cfg-gallery-arrow--prev" data-action="gallery-prev" aria-label="Vorige afbeelding">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
</button>`;
    const arrowNext = `<button class="cfg-gallery-arrow cfg-gallery-arrow--next" data-action="gallery-next" aria-label="Volgende afbeelding">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
</button>`;

    /* Skip label overlay on real product photos — the image itself carries the message. */
    const label = current.photo ? "" : `<span class="cfg-gallery-label">${_esc(current.label)} · ${view + 1}/${views.length}</span>`;

    /* Cap the thumbnail strip so big galleries never overflow the row. Show at
       most MAX_THUMBS (2 previews + 4 photos); when there are more, the last
       visible thumb gets a blurred "+N afbeeldingen" overlay. The arrows still
       cycle through every image. */
    const MAX_THUMBS = 6;
    const overflowCount = Math.max(0, views.length - MAX_THUMBS);
    const shown = overflowCount ? views.slice(0, MAX_THUMBS) : views;
    const lastIdx = shown.length - 1;
    const thumbs = shown.map((v, i) => {
      const isMoreTile = overflowCount > 0 && i === lastIdx;
      /* the "+N" tile counts as active for any of the capped-away images */
      const active = isMoreTile ? view >= lastIdx : i === view;
      let mini;
      if (v.photo)            mini = v.html;
      else if (v.key === "front") mini = this._renderFrontScene(true);
      else                    mini = this._renderProfileScene(true);
      const moreOverlay = isMoreTile
        ? `<span class="cfg-thumb-more" aria-hidden="true">+${overflowCount}</span>`
        : "";
      const cls = "cfg-gallery-thumb" + (active ? " is-active" : "") + (isMoreTile ? " cfg-gallery-thumb--more" : "");
      const aria = isMoreTile ? `Nog ${overflowCount} afbeeldingen` : _esc(v.label);
      return `<button class="${cls}" data-action="gallery-view" data-value="${i}" aria-label="${aria}" aria-pressed="${active}">
  ${mini}${moreOverlay}
</button>`;
    }).join("");

    const badgesOverlay = this._island.badgesHtml
      ? `<div class="cfg-gallery-badges">${this._island.badgesHtml}</div>`
      : "";

    /* wall-colour swatches — only over the front view */
    const wallSwatches = current.key === "front" ? this._renderWallSwatches() : "";

    return `<div class="cfg-gallery-viewer">
  <div class="cfg-gallery-stage">
    ${current.html}
    ${wallSwatches}
    ${badgesOverlay}
    ${arrowPrev}
    ${arrowNext}
    ${label}
  </div>
  <div class="cfg-gallery-thumbs">
    ${thumbs}
  </div>
</div>`;
  }

  /** Wall-colour picker overlaid bottom-left of the front gallery view.
   *  Collapsed to the active swatch; hover/focus reveals the rest + a label. */
  _renderWallSwatches() {
    const curRaw = this.sel.wall || (this._island.scene && this._island.scene.wallColor) || CFG_WALL_COLORS[1];
    const cur = String(curRaw).toLowerCase();
    const inPalette = CFG_WALL_COLORS.some((c) => c.toLowerCase() === cur);
    /* The current wall can fall outside the palette — a deep-link restored from
       the cart, the section's own default colour, etc. Without a matching swatch
       the collapse rule would hide every dot and leave an empty pill, so anchor
       it with the actual current colour shown as the active swatch. */
    let dots = "";
    if (!inPalette) {
      dots += `<button class="cfg-wall-swatch is-active" data-action="wall" data-value="${_esc(curRaw)}" style="background:${_esc(curRaw)}" aria-label="Huidige wandkleur" aria-pressed="true"></button>`;
    }
    /* active first so the collapsed pill is anchored on it and doesn't shift */
    const ordered = CFG_WALL_COLORS.filter((c) => c.toLowerCase() === cur)
      .concat(CFG_WALL_COLORS.filter((c) => c.toLowerCase() !== cur));
    dots += ordered.map((c) => {
      const on = inPalette && c.toLowerCase() === cur;
      return `<button class="cfg-wall-swatch${on ? " is-active" : ""}" data-action="wall" data-value="${c}" style="background:${c}" aria-label="Wandkleur ${c}" aria-pressed="${on}"></button>`;
    }).join("");
    return `<div class="cfg-wall-picker" role="group" aria-label="Muurkleur wijzigen">
  <span class="cfg-wall-picker-label" aria-hidden="true">Muurkleur wijzigen</span>
  <div class="cfg-wall-swatches">${dots}</div>
</div>`;
  }

  /** Render the FrontScene — wall + floor, then per-panel fills (material/colour/mirror)
   *  behind a parametric SVG frame that scales with the chosen width/height.
   *  @param {boolean} mini — thumbnail context (size controlled by CSS). */
  _renderFrontScene(mini) {
    const island = this._island;
    const sel    = this.sel;
    const scene  = island.scene || {};
    const wallColor  = sel.wall || scene.wallColor || "#E3E6E2";
    const floorStyle = scene.floorStyle || "Licht hout";
    const floorBg    = CFG_FLOOR_BG[floorStyle] || CFG_FLOOR_BG["Licht hout"];

    /* Camera constants shared by both branches */
    const FLOOR_LINE = 80;
    const zoomMode   = (island.ui && island.ui.galleryZoom) || "fit";

    /* ── paneel branch ─────────────────────────────────────────────────────
       PanelDoorFrame.renderDoor paints the face colour itself (panelHex) so
       there is NO transparent-opening fill layer — just the SVG door on the
       scene background. Width is simply sel.count × per-door width; no
       side-frame/mullion constants from SlidingDoorFrame are used here. */
    if (island.type === "paneel") {
      /* per-door width: total opening ÷ count (paneel doors span the full opening
         without the omlijst side-frame/mullion overhead) */
      const panelWmm = Math.round(sel.w / sel.count);
      const doorWmm  = sel.w;   // paneel: total width IS the door width
      const dt       = this._type;
      const frameSvg = (dt && dt.renderDoor)
        ? dt.renderDoor({
            count:        sel.count,
            color:        cfgLogic.frameColorKey(sel.frame),
            panelWidthMm: panelWmm,
            doorHeightMm: sel.h,
            pxPerMm:      1,
            idSuffix:     (mini ? "m" : "f") + sel.count,
            vlak:         sel.vlak,
            panelHex:     this._activeKleur()?.hex,
          })
        : "";
      const doorRefW = doorWmm * CFG_MM_TO_PCT;
      const doorRefH = sel.h   * CFG_MM_TO_PCT;
      const z = zoomMode === "fixed" ? 1 : Math.min(85 / doorRefW, 72 / doorRefH);
      if (!mini) this._layout = { doorRefW, doorRefH, z };
      const doorBox = `<div class="cfg-door" style="width:${doorRefW.toFixed(2)}%;height:${doorRefH.toFixed(2)}%;bottom:${(100 - FLOOR_LINE).toFixed(2)}%">
  <div class="cfg-door-frame">${frameSvg}</div>
</div>`;
      return `<div class="cfg-scene cfg-scene--front${mini ? " cfg-scene--mini" : ""}" style="background:${_esc(wallColor)}">
  <div class="cfg-scene-radial" aria-hidden="true"></div>
  <div class="cfg-zoom" style="transform:scale(${z.toFixed(4)});transform-origin:50% ${FLOOR_LINE}%">
    <div class="cfg-scene-ceiling" aria-hidden="true"></div>
    <div class="cfg-scene-floor" style="background:${floorBg}" aria-hidden="true"></div>
    <div class="cfg-scene-skirting" aria-hidden="true"></div>
    ${doorBox}
  </div>
</div>`;
    }

    /* ── omlijst branch (unchanged) ───────────────────────────────────────── */
    const panel    = this._visualPanel();
    const panelHex = (panel && panel.hex) ? panel.hex : "#BFC2AC";
    const material = panel ? panel.material_url : "";

    /* frame constants from the module (single source of truth); fallback if not loaded */
    const SDF = (typeof window !== "undefined") ? window.SlidingDoorFrame : null;
    const F   = SDF ? SDF.FRAME_MM_DEFAULTS
                    : { sideFrameW: 16, mullionW: 5, topCapH: 55, topGapH: 1.5, topChannelH: 8, botTrackH: 15 };

    const fm          = island.frameModel || {};
    const maxPerDoor  = fm.maxPerDoor || 1000;
    const maxHeight   = fm.maxHeight  || 2850;
    const topAssembly = F.topCapH + F.topGapH + F.topChannelH;
    const maxPanelHmm = maxHeight - topAssembly - F.botTrackH;

    const panelWmm = cfgLogic.panelWidthFromTotal(sel.w, sel.count, F);
    const innerHmm = sel.h - topAssembly - F.botTrackH;
    const doorWmm  = 2 * F.sideFrameW + sel.count * panelWmm + (sel.count - 1) * F.mullionW;

    const openings = cfgLogic.panelOpenings(sel.count, panelWmm, sel.h, F);
    const crop     = cfgLogic.materialCrop(panelWmm, innerHmm, maxPerDoor, maxPanelHmm);

    /* per-panel fill behind the frame: mirror > material image > solid colour.
       Bleed each fill a few mm under the (opaque) frame so the panel tucks behind
       the side profiles/mullions — otherwise a sub-pixel seam of the background
       shows at the panel edges (most visible on the far-left). */
    const bxPct = 3 / doorWmm * 100;   // horizontal bleed (under side frame + mullions)
    const byPct = 2 / sel.h   * 100;   // vertical bleed (under channel + track)
    const fills = openings.map((r, i) => {
      const style = `left:${(r.xPct - bxPct).toFixed(3)}%;top:${(r.yPct - byPct).toFixed(3)}%;width:${(r.wPct + 2 * bxPct).toFixed(3)}%;height:${(r.hPct + 2 * byPct).toFixed(3)}%`;
      if (sel.mirrors.includes(i)) {
        return `<div class="cfg-fill cfg-fill--mirror" style="${style}"><div class="cfg-scene-mirror-shine" aria-hidden="true"></div></div>`;
      }
      if (material) {
        const flip = CFG_PANEL_FLIPS[i % CFG_PANEL_FLIPS.length];
        const tf   = "translateX(-50%)" + (flip ? " " + flip : "");
        return `<div class="cfg-fill cfg-fill--material" style="${style}"><img src="${_esc(material)}" alt="" aria-hidden="true" class="cfg-fill-img" style="width:${crop.w.toFixed(2)}%;height:${crop.h.toFixed(2)}%;transform:${tf}"></div>`;
      }
      return `<div class="cfg-fill" style="${style};background:${panelHex}"></div>`;
    }).join("");

    /* parametric frame SVG on top (transparent openings, semi-transparent shadows).
       Routed through the door-type module so a second type can swap the renderer;
       omlijst's renderDoor delegates to SlidingDoorFrame and ignores `vlak`. */
    const dt = this._type;
    const frameSvg = (dt && dt.renderDoor)
      ? dt.renderDoor({ count: sel.count, color: cfgLogic.frameColorKey(sel.frame), panelWidthMm: panelWmm, doorHeightMm: sel.h, pxPerMm: 1, idSuffix: (mini ? "m" : "f") + sel.count, vlak: sel.vlak })
      : "";

    /* Camera model: the door is drawn at a fixed real-world scale, then a camera
       zoom frames it. "fit" zooms so the door fills the view; "fixed" keeps zoom = 1
       (constant scale). The whole scene (door, plinth, floor) lives inside .cfg-zoom
       and scales together, so resizing reads as a real camera move, not a jump. */
    const doorRefW   = doorWmm * CFG_MM_TO_PCT;    // door size at zoom 1 (% of stage)
    const doorRefH   = sel.h   * CFG_MM_TO_PCT;
    const z = zoomMode === "fixed" ? 1 : Math.min(85 / doorRefW, 72 / doorRefH);
    if (!mini) this._layout = { doorRefW, doorRefH, z };

    const doorBox = `<div class="cfg-door" style="width:${doorRefW.toFixed(2)}%;height:${doorRefH.toFixed(2)}%;bottom:${(100 - FLOOR_LINE).toFixed(2)}%">
  <div class="cfg-door-fills">${fills}</div>
  <div class="cfg-door-frame">${frameSvg}</div>
</div>`;

    return `<div class="cfg-scene cfg-scene--front${mini ? " cfg-scene--mini" : ""}" style="background:${_esc(wallColor)}">
  <div class="cfg-scene-radial" aria-hidden="true"></div>
  <div class="cfg-zoom" style="transform:scale(${z.toFixed(4)});transform-origin:50% ${FLOOR_LINE}%">
    <div class="cfg-scene-ceiling" aria-hidden="true"></div>
    <div class="cfg-scene-floor" style="background:${floorBg}" aria-hidden="true"></div>
    <div class="cfg-scene-skirting" aria-hidden="true"></div>
    ${doorBox}
  </div>
</div>`;
  }

  /** Animate the camera zoom + door size from the previous layout to the current
   *  one so a size change reads as a smooth camera move (the whole scene scales).
   *  Re-render recreates the nodes, so set previous values, reflow, then target. */
  _applyLayoutTransition() {
    const stage = this.querySelector(".cfg-gallery-stage") || this.querySelector(".cfg-embed-stage");
    const t = this._layout;
    if (!stage || !t) return;
    const zoom = stage.querySelector(".cfg-zoom");
    const door = stage.querySelector(".cfg-door");
    const prev = this._prevLayout;
    this._prevLayout = t;

    const changed = prev && (prev.z !== t.z || prev.doorRefW !== t.doorRefW || prev.doorRefH !== t.doorRefH);
    if (!changed || !zoom || !door || typeof zoom.animate !== "function") return;

    /* respect reduced-motion */
    if (typeof window !== "undefined" && window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    /* Web Animations API: reliably animates the freshly-rebuilt nodes from the
       previous layout to the current inline target (the whole scene zooms). */
    const opts = { duration: 600, easing: "ease-in-out" };
    zoom.animate(
      [{ transform: `scale(${prev.z})` }, { transform: `scale(${t.z})` }],
      opts
    );
    door.animate(
      [
        { width: prev.doorRefW + "%", height: prev.doorRefH + "%" },
        { width: t.doorRefW + "%", height: t.doorRefH + "%" },
      ],
      opts
    );
  }

  /** Render the ProfileScene — panel bg + CSS profile bar + caption. */
  _renderProfileScene(mini) {
    const island   = this._island;
    const sel      = this.sel;

    /* paneel branch: use the active kleur hex for the background */
    if (island.type === "paneel") {
      const kleur    = this._activeKleur();
      const panelHex = (kleur && kleur.hex) ? kleur.hex : "#E8E8E8";
      const lum      = cfgLogic.lum(panelHex);
      const barBg    = CFG_PROFILE_BAR[sel.frame] || CFG_PROFILE_BAR["Zwart"];
      const captionColor = lum > 0.5 ? "rgba(29,29,27,0.55)" : "rgba(255,255,255,0.65)";
      const caption = mini ? "" : `<span class="cfg-profile-caption" style="color:${captionColor}">profieldetail · indicatieve weergave</span>`;
      return `<div class="cfg-scene cfg-scene--profile${mini ? " cfg-scene--mini" : ""}" style="background:${panelHex}">
  <div class="cfg-scene-profile-light" aria-hidden="true"></div>
  <div class="cfg-scene-profile-bar" style="background:${barBg}" aria-hidden="true">
    <div class="cfg-scene-profile-shadow" aria-hidden="true"></div>
    <div class="cfg-scene-profile-highlight" aria-hidden="true"></div>
  </div>
  ${caption}
</div>`;
    }

    /* omlijst branch (unchanged) */
    const panel    = island.panels.find((p) => p.id === sel.panel) || island.panels[0];
    const panelHex = panel ? panel.hex : "#BFC2AC";
    const lum      = cfgLogic.lum(panelHex);
    const barBg    = CFG_PROFILE_BAR[sel.frame] || CFG_PROFILE_BAR["Zwart"];
    const captionColor = lum > 0.5 ? "rgba(29,29,27,0.55)" : "rgba(255,255,255,0.65)";

    const caption = mini ? "" : `<span class="cfg-profile-caption" style="color:${captionColor}">profieldetail · indicatieve weergave — set-foto volgt</span>`;

    return `<div class="cfg-scene cfg-scene--profile${mini ? " cfg-scene--mini" : ""}" style="background:${panelHex}">
  <div class="cfg-scene-profile-light" aria-hidden="true"></div>
  <div class="cfg-scene-profile-bar" style="background:${barBg}" aria-hidden="true">
    <div class="cfg-scene-profile-shadow" aria-hidden="true"></div>
    <div class="cfg-scene-profile-highlight" aria-hidden="true"></div>
  </div>
  ${caption}
</div>`;
  }

  /* ── group wrapper ─────────────────────────────────── */
  _renderGroup(num, title, hint, helpText, body) {
    const hintHtml  = hint  ? `<span class="cfg-group-hint">${_esc(hint)}</span>` : "";
    const helpHtml  = helpText ? this._renderHelp(helpText) : "";
    /* num=0 / null / "" → unnumbered group (e.g. vlakverdeling is a stylistic
       pre-step that doesn't take a step number). */
    const numHtml = num ? `<span class="cfg-group-num">${num}</span>` : "";
    return `
<div class="cfg-group${num ? "" : " cfg-group--unnumbered"}">
  <div class="cfg-group-header">
    ${numHtml}
    <span class="cfg-group-title">${_esc(title)}</span>
    ${helpHtml}
    ${hintHtml}
  </div>
  <div class="cfg-group-body">${body}</div>
</div>`;
  }

  _renderHelp(text) {
    return `
<span class="cfg-help">
  <button class="cfg-help-btn" aria-label="Meer informatie" tabindex="0">?</button>
  <span role="tooltip" class="cfg-help-tooltip">
    ${_esc(text)}
    <span class="cfg-help-arrow" aria-hidden="true"></span>
  </span>
</span>`;
  }

  /* ── panel swatches ────────────────────────────────── */
  _renderPanelSwatches(panels, sel, round, labels) {
    /* Visual "active" swatch: when full-spiegel or partial-mirror mode swaps the
       internal sel.panel to "spiegel-alles" / "spiegel-decor:<id>" (neither is a
       swatch), fall back to the underlying base decor so the picker still shows
       where the user will return to (or is currently mirroring). */
    const visualPanelId = sel.panel === "spiegel-alles"
      ? (this._prevPanelBeforeSpiegel || sel.panel)
      : (typeof sel.panel === "string" && sel.panel.startsWith("spiegel-decor:"))
        ? sel.panel.slice("spiegel-decor:".length)
        : sel.panel;
    return `<div class="cfg-swatches">${panels.map((p) => {
      const active = p.id === visualPanelId;
      /* A material photo (wood decor) always wins as the swatch sample; else hex. */
      const hasHex = p.hex && p.hex.trim().length > 0;
      const bg = p.material_url
        ? `center / cover no-repeat url('${_esc(p.material_url)}')`
        : `linear-gradient(135deg, ${hasHex ? p.hex : "#CCCCCC"}, ${hasHex ? p.hex : "#CCCCCC"} 60%, rgba(29,29,27,0.08))`;
      return `<div class="cfg-swatch-wrap${labels ? " cfg-swatch-wrap--labeled" : ""}">
  <button
    class="cfg-swatch${active ? " is-active" : ""}${round ? " cfg-swatch--round" : ""}"
    data-action="panel"
    data-value="${_esc(p.id)}"
    style="background:${bg}"
    aria-label="${_esc(p.label)}"
    aria-pressed="${active}"
    title="${_esc(p.label)}"
  ></button>
  ${labels ? `<span class="cfg-swatch-label${active ? " is-active" : ""}">${_esc(p.label)}</span>` : ""}
</div>`;
    }).join("")}</div>`;
  }

  /* ── paneel vlakverdeling picker — wireframe SVG icon per pattern ───── */
  _renderVlakSwatches() {
    const island = this._island;
    const sel    = this.sel;
    /* Canonical display order. Vlakverdelingen are sorted into this order
       before rendering; any unknown ids fall through to the end (preserving
       their relative order). */
    const VLAK_ORDER = ['Eenvlaks','Tweevlaks','Drievlaks','Vlak','Verticaal gelijnd','Horizontaal gelijnd'];
    const vlakverdelingen = (island.vlakverdelingen || []).slice().sort((a, b) => {
      const ai = VLAK_ORDER.indexOf(a.id);
      const bi = VLAK_ORDER.indexOf(b.id);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    const icons = (typeof window !== "undefined" && window.PanelDoorFrame && window.PanelDoorFrame.VLAK_ICONS) || {};
    /* Dev/preview mode: when the section's `preview_all_vlakken` toggle is on,
       all 6 canonical vlakverdelingen are emitted even without a backing product,
       and the disabled state is suppressed so the merchant can switch between
       them to verify the renderer. Combos without a product still resolve to
       null in _resolveProduct, so price + add-to-cart will read unavailable. */
    const previewAll = !!island.preview_all_vlakken;
    return `<div class="cfg-swatches cfg-swatches--vlak">${vlakverdelingen.map((v) => {
      const active   = v.id === sel.vlak;
      const matched  = island.matrix && island.matrix[sel.kleur + "|" + v.id];
      const disabled = !previewAll && !matched;
      const icon     = icons[v.id] || "";
      return `<div class="cfg-swatch-wrap cfg-swatch-wrap--labeled">
  <button
    class="cfg-swatch cfg-swatch--vlak${active ? " is-active" : ""}"
    data-action="vlak"
    data-value="${_esc(v.id)}"
    aria-label="${_esc(v.label)}"
    aria-pressed="${active}"
    title="${_esc(v.label)}"
    ${disabled ? 'disabled aria-disabled="true"' : ""}
  >${icon}</button>
  <span class="cfg-swatch-label${active ? " is-active" : ""}">${_esc(v.label)}</span>
</div>`;
    }).join("")}</div>`;
  }

  /* ── paneel kleur swatches ──────────────────────────── */
  _renderKleurSwatches() {
    const island = this._island;
    const sel    = this.sel;
    const kleuren = island.kleuren || [];
    return `<div class="cfg-swatches">${kleuren.map((k) => {
      const active = k.id === sel.kleur;
      const hasHex = k.hex && k.hex.trim().length > 0;
      const bg = `linear-gradient(135deg, ${hasHex ? k.hex : "#CCCCCC"}, ${hasHex ? k.hex : "#CCCCCC"} 60%, rgba(29,29,27,0.08))`;
      return `<div class="cfg-swatch-wrap cfg-swatch-wrap--labeled">
  <button
    class="cfg-swatch cfg-swatch--round${active ? " is-active" : ""}"
    data-action="kleur"
    data-value="${_esc(k.id)}"
    style="background:${bg}"
    aria-label="${_esc(k.label)}"
    aria-pressed="${active}"
    title="${_esc(k.label)}"
  ></button>
  <span class="cfg-swatch-label${active ? " is-active" : ""}">${_esc(k.label)}</span>
</div>`;
    }).join("")}</div>`;
  }

  /* ── dimension fields ──────────────────────────────── */
  _renderDimensions(sel, island) {
    const wMin = cfgLogic.widthMin(this._ranges), wMax = cfgLogic.widthMax(this._ranges);
    const hMin = island.height?.min ?? this._type.heightMin;
    const hMax = island.height?.max ?? this._type.heightMax;
    return `<div class="cfg-dimensions">
  <label class="cfg-dim-label">
    <span class="cfg-dim-label-text">Breedte</span>
    <input class="cfg-dim-input" type="number" data-action="width"
      value="${sel.w}" min="${wMin}" max="${wMax}" step="1" aria-label="Breedte in mm" />
    <span class="cfg-dim-unit">mm</span>
    <span class="cfg-dim-helper">Totale breedte van de opening</span>
  </label>
  <label class="cfg-dim-label">
    <span class="cfg-dim-label-text">Hoogte</span>
    <input class="cfg-dim-input" type="number" data-action="height"
      value="${sel.h}" min="${hMin}" max="${hMax}" step="1" aria-label="Hoogte in mm" />
    <span class="cfg-dim-unit">mm</span>
    <span class="cfg-dim-helper">Vloer tot plafond, max. ${hMax} mm</span>
  </label>
</div>`;
  }

  /* ── door count buttons ────────────────────────────── */
  _renderDoorCount(sel, validCounts, round, labels) {
    return `<div class="cfg-door-counts">${CFG_COUNTS_EL.map((n) => {
      const active   = n === sel.count;
      const disabled = !validCounts.includes(n);
      const range = cfgLogic.countRange(n, this._ranges) || [0, 0];
      const minW = range[0], maxW = range[1];
      const title = disabled
        ? `Mogelijk bij ${minW}–${maxW} mm`
        : `${n}-deurs`;
      const glyphs = Array.from({ length: n }).map(() =>
        `<span class="cfg-door-glyph"></span>`
      ).join("");
      return `<div class="cfg-door-count-wrap">
  <button
    class="cfg-door-count-btn${active ? " is-active" : ""}${round ? " cfg-door-count-btn--round" : ""}"
    data-action="count"
    data-value="${n}"
    aria-pressed="${active}"
    ${disabled ? 'disabled aria-disabled="true"' : ""}
    title="${_esc(title)}"
  >
    <span class="cfg-door-glyphs cfg-door-glyphs--${n}">${glyphs}</span>
    <span class="cfg-door-count-num">${n}</span>
  </button>
  ${labels ? `<span class="cfg-door-count-range">${minW}–${maxW} mm</span>` : ""}
</div>`;
    }).join("")}</div>`;
  }

  /* ── frame swatches ────────────────────────────────── */
  _renderFrameSwatches(frames, sel, round, labels) {
    return `<div class="cfg-swatches">${frames.map((f) => {
      const active = f.id === sel.frame;
      /* I4: sanitize merchant free-text swatch_css before interpolating into style attribute */
      const safeCss = _sanitizeCss(f.swatch_css);
      return `<div class="cfg-swatch-wrap${labels ? " cfg-swatch-wrap--labeled" : ""}">
  <button
    class="cfg-swatch${active ? " is-active" : ""}${round ? " cfg-swatch--round" : ""}"
    data-action="frame"
    data-value="${_esc(f.id)}"
    style="background:${safeCss}"
    aria-label="${_esc(f.label)}"
    aria-pressed="${active}"
    title="${_esc(f.label)}"
  ></button>
  ${labels ? `<span class="cfg-swatch-label${active ? " is-active" : ""}">${_esc(f.label)}</span>` : ""}
</div>`;
    }).join("")}</div>`;
  }

  /* ── mirror picker ─────────────────────────────────── */
  _renderMirrorPicker(sel, mirrorCents) {
    /* Once every door is already mirrored (full-spiegel active) there is no
       further "+per deur" delta to earn — the whole door product IS the mirror
       price already, so the hint would be misleading. Show it again as soon as
       at least one door is unmirrored (partial / none). */
    const showDelta  = sel.mirrors.length < sel.count;
    const priceLabel = (showDelta && mirrorCents > 0)
      ? "+ " + cfgLogic.euro(mirrorCents) + " per deur"
      : "";
    const doors = Array.from({ length: sel.count }).map((_, i) => {
      const on = sel.mirrors.includes(i);
      return `<div class="cfg-mirror-slot">
  <button
    class="cfg-mirror-btn${on ? " is-active" : ""}"
    data-action="mirror"
    data-value="${i}"
    aria-pressed="${on}"
    aria-label="Spiegel op deur ${i + 1}"
    title="Spiegel op deur ${i + 1}"
  >${on ? '<span class="cfg-mirror-shine" aria-hidden="true"></span>' : ""}</button>
  <span class="cfg-mirror-label${on ? " is-active" : ""}">Deur ${i + 1}</span>
</div>`;
    }).join("");
    return `<div class="cfg-mirror-picker">
  ${doors}
  ${priceLabel ? `<span class="cfg-mirror-price">${_esc(priceLabel)}</span>` : ""}
</div>`;
  }

  /* ── soft-close switch ─────────────────────────────── */
  _renderSoftClose(sel, softCents, softCompare) {
    const on = sel.soft;
    const oldHtml = (softCompare && softCompare > softCents)
      ? `<s class="cfg-switch-price-old">${_esc(cfgLogic.euro(softCompare))}</s> ` : "";
    /* soft-close is per door — show "{count}× €…" */
    const priceHtml = softCents > 0
      ? `<span class="cfg-switch-price">${oldHtml}<span class="cfg-switch-mult">${sel.count}×</span> ${_esc(cfgLogic.euro(softCents))}</span>`
      : "";
    return `<button
  class="cfg-switch${on ? " is-on" : ""}"
  data-action="soft"
  role="switch"
  aria-checked="${on}"
>
  <span class="cfg-switch-track" aria-hidden="true">
    <span class="cfg-switch-thumb"></span>
  </span>
  <span class="cfg-switch-body">
    <span class="cfg-switch-label">Soft-close systeem</span>
    <span class="cfg-switch-sub">Deuren remmen af en sluiten zacht</span>
  </span>
  ${priceHtml}
</button>`;
  }

  /* ── montageservice switch ─────────────────────────── */
  _renderMontage(sel, montageCents, montageCompare) {
    const on = sel.montage;
    const oldHtml = (montageCompare && montageCompare > montageCents)
      ? `<s class="cfg-switch-price-old">${_esc(cfgLogic.euro(montageCompare))}</s> ` : "";
    const priceHtml = montageCents > 0
      ? `<span class="cfg-switch-price">${oldHtml}+ ${_esc(cfgLogic.euro(montageCents))}</span>`
      : "";
    return `<button
  class="cfg-switch${on ? " is-on" : ""}"
  data-action="montage"
  role="switch"
  aria-checked="${on}"
>
  <span class="cfg-switch-track" aria-hidden="true">
    <span class="cfg-switch-thumb"></span>
  </span>
  <span class="cfg-switch-body">
    <span class="cfg-switch-label">Montageservice</span>
    <span class="cfg-switch-sub">Wij plaatsen je schuifdeuren vakkundig</span>
  </span>
  ${priceHtml}
</button>`;
  }

  /* ── sticky buy bar ────────────────────────────────── */
  _renderStickyBar(pricing, panelName, sel, activePanel, unavailable) {
    /* I5: when no matched variant show "Op aanvraag" and disable the CTA */
    const totalHtml = unavailable
      ? `<span class="cfg-bar-total cfg-bar-total--unavailable">Op aanvraag</span>`
      : `<span class="cfg-bar-total">${_esc(cfgLogic.euro(pricing.total))}</span>`;
    const btnDisabled = unavailable ? " disabled" : "";

    /* summary chips — mirror the design-reference StickyBuyBar chips */
    const frameLabel = sel.frame ? sel.frame.toLowerCase() : "";
    const chips = [
      _esc(panelName),
      _esc(sel.w + " \xd7 " + sel.h + " mm"),
      _esc(sel.count + "-deurs"),
      _esc("Profiel " + frameLabel),
    ];
    if (sel.mirrors.length && sel.panel !== "spiegel-alles") chips.push(_esc("Spiegel \xd7 " + sel.mirrors.length));
    if (sel.soft)           chips.push("Soft-close");
    if (sel.montage)        chips.push("Montage");

    const chipHtml = chips
      .map((c) => `<span class="cfg-bar-chip">${c}</span>`)
      .join("");

    /* ── mobile presentation: thumbnail + expandable summary + price + CTA ── */
    const rows = [
      ["Paneelkleur", panelName],
      ["Maten", sel.w + " \xd7 " + sel.h + " mm"],
      ["Aantal deuren", sel.count + "-deurs"],
      ["Profielkleur", sel.frame],
    ];
    if (sel.mirrors.length && sel.panel !== "spiegel-alles") rows.push(["Spiegel", sel.mirrors.length + " deur" + (sel.mirrors.length > 1 ? "en" : "")]);
    rows.push(["Soft-close", sel.soft ? "Ja" : "Nee"]);
    rows.push(["Montage", sel.montage ? "Ja" : "Nee"]);
    const rowHtml = rows.map(([k, v]) =>
      `<div class="cfg-bar-m-row"><span>${_esc(k)}</span><span>${_esc(v)}</span></div>`).join("");
    const summaryTxt = sel.count + "-deurs \xb7 " + (sel.frame || "") +
      ((sel.mirrors.length && sel.panel !== "spiegel-alles") ? " \xb7 " + sel.mirrors.length + "\xd7 spiegel" : "");
    const mTotal = unavailable ? "Op aanvraag" : _esc(cfgLogic.euro(pricing.total));
    const chev = `<svg class="cfg-bar-m-chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6"/></svg>`;
    const openClass = this._barOpen ? " is-open" : "";

    return `<div class="cfg-sticky-bar" role="region" aria-label="Samenvatting en bestellen">
  <div class="cfg-bar-inner cfg-bar-desktop">
    <div class="cfg-bar-chips" aria-hidden="true">${chipHtml}</div>
    ${totalHtml}
    <button class="cfg-bar-btn" data-action="add-to-cart"${btnDisabled}>In winkelwagen</button>
  </div>
  <div class="cfg-bar-mobile${openClass}">
    <div class="cfg-bar-m-summary"><div class="cfg-bar-m-rows">${rowHtml}</div></div>
    <div class="cfg-bar-m-main">
      <button class="cfg-bar-m-thumb" data-action="bar-thumb" aria-label="Bekijk het voorbeeld">
        ${this._renderFrontScene(true)}
        <span class="cfg-bar-m-thumb-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m6 15 6-6 6 6"/></svg></span>
      </button>
      <button class="cfg-bar-m-toggle" data-action="bar-expand" aria-expanded="${this._barOpen ? "true" : "false"}">
        <span class="cfg-bar-m-sum">${_esc(summaryTxt)} ${chev}</span>
        <span class="cfg-bar-m-price">${mTotal}</span>
      </button>
      <button class="cfg-bar-btn cfg-bar-m-cta" data-action="add-to-cart"${btnDisabled}>In winkelwagen</button>
    </div>
  </div>
</div>`;
  }

  /* ── price block ───────────────────────────────────── */
  _renderPriceBlock(pricing, ui, unavailable) {
    /* I5: when no matched variant, show "Op aanvraag" instead of €0 */
    if (unavailable) {
      return `<div class="cfg-price-block">
  <div class="cfg-price-row">
    <span class="cfg-price-total cfg-price-total--unavailable">Op aanvraag</span>
    <span class="cfg-price-vat">Niet beschikbaar voor deze configuratie</span>
  </div>
  <div class="cfg-cta-row">
    <button class="cfg-btn-primary" data-action="add-to-cart" disabled>In winkelwagen</button>
    <!-- <button class="cfg-btn-outline" data-action="save-design">Bewaar ontwerp</button> -->
  </div>
</div>`;
    }

    const total     = cfgLogic.euro(pricing.total);
    const compareAt = pricing.compareAt ? cfgLogic.euro(pricing.compareAt) : null;
    const breakdown = ui.showPriceBreakdown !== false;

    const lineRows = breakdown && pricing.lines.length > 0
      ? `<div class="cfg-price-lines">${pricing.lines.map((l) => {
          /* (N) suffix is shown whenever count is truthy, including 1 — approved layout,
             not just when count > 1 (that would hide it for e.g. "Montageservice (1)"). */
          const countPart = l.count ? ` (${l.count})` : "";
          const hasOld  = l.compareAt && l.compareAt > l.amount;
          const oldHtml = hasOld ? `<s class="cfg-price-line-old">${_esc(cfgLogic.euro(l.compareAt))}</s> ` : "";
          return `<div class="cfg-price-line cfg-price-line--${l.key}">
            <span class="cfg-price-line-label">${_esc(l.label)}${countPart}</span>
            <span class="cfg-price-line-amt">${oldHtml}${_esc(cfgLogic.euro(l.amount))}</span>
          </div>`;
        }).join("")}</div>`
      : "";

    const strikeHtml = compareAt
      ? `<span class="cfg-price-compare">${_esc(compareAt)}</span>`
      : "";

    const usps = [
      "Binnen 6–8 weken in huis, gratis geleverd",
      "10 jaar metaalgarantie",
      "Betalen met 25% aanbetaling",
    ];

    return `<div class="cfg-price-block">
  <div class="cfg-price-row">
    <span class="cfg-price-total">${_esc(total)}</span>
    ${strikeHtml}
    <span class="cfg-price-vat">incl. btw</span>
  </div>
  ${lineRows}
  <div class="cfg-cta-row">
    <button class="cfg-btn-primary" data-action="add-to-cart">In winkelwagen</button>
    <!-- <button class="cfg-btn-outline" data-action="save-design">Bewaar ontwerp</button> -->
  </div>
  <div class="cfg-usp-row">
    ${usps.map((u) => `<span class="cfg-usp-item">${_checkSvg()}${_esc(u)}</span>`).join("")}
  </div>
</div>`;
  }

  /* ── event binding ─────────────────────────────────── */
  _bindEvents() {
    /* delegate all button/input events to one handler on the root */
    this.removeEventListener("click",  this._onClick);
    this.removeEventListener("change", this._onChange);
    this.removeEventListener("blur",   this._onDimBlur, true);

    this._onClick    = this._handleClick.bind(this);
    this._onChange   = this._handleChange.bind(this);
    this._onDimBlur  = this._handleDimBlur.bind(this);

    this.addEventListener("click",  this._onClick);
    this.addEventListener("change", this._onChange);
    this.addEventListener("blur",   this._onDimBlur, true);

    /* I3: help tooltip hover — remove then re-add bound refs (no multiply-on-render leak) */
    this.removeEventListener("mouseenter", this._onHelpHover, true);
    this.removeEventListener("mouseleave", this._onHelpHover, true);
    this.removeEventListener("focusin",    this._onHelpHover, true);
    this.removeEventListener("focusout",   this._onHelpHover, true);

    this.addEventListener("mouseenter", this._onHelpHover, true);
    this.addEventListener("mouseleave", this._onHelpHover, true);
    this.addEventListener("focusin",    this._onHelpHover, true);
    this.addEventListener("focusout",   this._onHelpHover, true);
  }

  _handleClick(e) {
    const btn = e.target.closest("[data-action]");
    if (!btn) return;
    const action = btn.dataset.action;
    const value  = btn.dataset.value;

    if (action === "panel")  { this._setPanel(value); return; }
    if (action === "vlak")   { this._setVlak(value); return; }
    if (action === "kleur")  { this._setKleur(value); return; }
    if (action === "count")  { this._setCount(Number(value)); return; }
    if (action === "frame")  { this._setFrame(value); return; }
    if (action === "mirror") { this._toggleMirror(Number(value)); return; }
    if (action === "soft")   { this._toggleSoft(); return; }
    if (action === "montage") { this._toggleMontage(); return; }
    if (action === "wall")   { this._setWall(value); return; }
    if (action === "gallery-prev") { this._galleryStep(-1); return; }
    if (action === "gallery-next") { this._galleryStep(1); return; }
    if (action === "gallery-view") { this._gallerySetView(Number(value)); return; }
    if (action === "bar-expand")   { this._toggleBar(); return; }
    if (action === "bar-thumb")    { this._scrollToGallery(); return; }
    if (action === "add-to-cart")  { this._addToCart(); return; }
    if (action === "design-embed") { this._designEmbed(); return; }
    /* save-design — static no-op (YAGNI) */
  }

  /** Hero embed CTA: open the chosen colour's product page (or the configured
   *  fallback) with the current selection encoded as a deep-link. */
  _designEmbed() {
    const island = this._island;
    const panel = island.panels.find((p) => p.id === this.sel.panel) || island.panels[0];
    let url = (panel && panel.product_url) || (island.ui && island.ui.embedFallbackUrl) || "/";
    const sep = url.indexOf("?") === -1 ? "?" : "&";
    if (typeof window !== "undefined") window.location.href = url + sep + this._cfgQueryString();
  }

  /** Toggle the mobile buy-bar summary in place (no re-render, so state persists). */
  _toggleBar() {
    this._barOpen = !this._barOpen;
    const bar = this.querySelector(".cfg-bar-mobile");
    if (bar) bar.classList.toggle("is-open", this._barOpen);
    const tg = this.querySelector(".cfg-bar-m-toggle");
    if (tg) tg.setAttribute("aria-expanded", this._barOpen ? "true" : "false");
  }

  /** Mobile: tapping the buy-bar thumbnail scrolls the gallery into view. */
  _scrollToGallery() {
    const g = this.querySelector(".cfg-gallery");
    if (g && g.scrollIntoView) g.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  _handleChange(e) {
    const el = e.target;
    if (!el.dataset.action) return;
    const action = el.dataset.action;
    if (action === "width")  { this._applyWidth(Number(el.value)); return; }
    if (action === "height") { this._applyHeight(Number(el.value)); return; }
  }

  /* commit dimension on blur (handles manual typing without re-render on every keystroke) */
  _handleDimBlur(e) {
    const el = e.target;
    if (!el.dataset.action) return;
    if (el.dataset.action === "width")  { this._applyWidth(Number(el.value)); return; }
    if (el.dataset.action === "height") { this._applyHeight(Number(el.value)); return; }
  }

  _handleHelpHover(e) {
    const helpBtn = e.target.closest(".cfg-help");
    if (!helpBtn) return;
    const tooltip = helpBtn.querySelector(".cfg-help-tooltip");
    if (!tooltip) return;
    const open = e.type === "mouseenter" || e.type === "focusin";
    tooltip.classList.toggle("is-visible", open);
  }

  /* ── state mutators ────────────────────────────────── */
  _setPanel(id) {
    const island = this._island;
    const panel  = island.panels.find((p) => p.id === id);
    if (!panel) return;
    /* In the hero embed, colour selection only re-tints the preview locally;
       navigation happens on the "Ontwerp verder" CTA instead. */
    const embed = island.ui && island.ui.embed;

    this.sel = { ...this.sel, panel: id };
    this._notice = null;

    /* Preserve mirror state across decor switches: if the user had ≥1 spiegel
       selected, re-fire the switch so we land on the NEW decor's spiegel_product
       (or spiegel-alles) rather than the new base decor. Skips URL rewrite so
       we can emit the final resolved product URL once below. */
    this._maybeSwitchSpiegel({ skipUrl: true });

    /* URL follows the resolved active panel (base / spiegel-decor / spiegel-alles),
       never the raw picked base — otherwise partial-mirror + decor change would
       land on the base URL and break Aanpassen deep-link. */
    if (!embed && typeof history !== "undefined" && history.replaceState) {
      const active = this._activePanel();
      const url = (active && active.product_url) || panel.product_url;
      if (url) {
        const sep = url.indexOf("?") === -1 ? "?" : "&";
        try { history.replaceState(null, "", url + sep + this._cfgQueryString()); } catch (e) { /* noop */ }
      }
    }

    this._render();
  }

  _setKleur(id) {
    const island = this._island;
    if (!island.kleuren || !island.kleuren.some((k) => k.id === id)) return;
    this.sel     = { ...this.sel, kleur: id };
    this._notice = null;
    const product = this._resolveProduct();
    if (product && product.url) {
      const sep = product.url.indexOf("?") === -1 ? "?" : "&";
      try { history.replaceState(null, "", product.url + sep + this._cfgQueryString()); } catch (e) { /* noop */ }
    }
    this._render();
  }

  _setVlak(id) {
    const island = this._island;
    if (!island.vlakverdelingen || !island.vlakverdelingen.some((v) => v.id === id)) return;
    this.sel     = { ...this.sel, vlak: id };
    this._notice = null;
    const product = this._resolveProduct();
    if (product && product.url) {
      const sep = product.url.indexOf("?") === -1 ? "?" : "&";
      try { history.replaceState(null, "", product.url + sep + this._cfgQueryString()); } catch (e) { /* noop */ }
    }
    this._render();
  }

  _applyWidth(w) {
    const wMin = cfgLogic.widthMin(this._ranges), wMax = cfgLogic.widthMax(this._ranges);
    w = Math.max(wMin, Math.min(wMax, w || wMin));

    const valid    = cfgLogic.validCounts(w, this._ranges);
    let count      = this.sel.count;
    let notice     = null;

    if (valid.length && !valid.includes(count)) {
      count = cfgLogic.nearestCount(w, count, this._ranges);
      const rangeStr = valid.length > 1
        ? valid.join(" of ") + " deuren mogelijk"
        : "past alleen een " + count + "-deurs opstelling";
      notice = `Aantal deuren aangepast naar ${count} — bij ${w} mm ${rangeStr}.`;
    }

    /* No change (e.g. the blur that fires right after a change commits the same
       value) — skip the re-render so it doesn't wipe the resize animation. */
    if (w === this.sel.w && count === this.sel.count) { this._notice = notice; return; }

    /* drop out-of-range mirrors */
    let mirrors = this.sel.mirrors.filter((i) => i < count);
    /* Full-spiegel: expand mirrors to cover any new door added by the width change. */
    if (this.sel.panel === "spiegel-alles") {
      mirrors = Array.from({ length: count }, (_, i) => i);
    }
    this.sel    = { ...this.sel, w, count, mirrors };
    this._notice = notice;
    this._maybeSwitchSpiegel();
    this._render();
  }

  _applyHeight(h) {
    const island = this._island;
    const hMin = island.height?.min ?? this._type.heightMin;
    const hMax = island.height?.max ?? this._type.heightMax;
    h = Math.max(hMin, Math.min(hMax, h || hMin));
    if (h === this.sel.h) return;  // no change → don't re-render (preserves the animation)
    this.sel    = { ...this.sel, h };
    this._notice = null;
    this._render();
  }

  _setCount(n) {
    if (!CFG_COUNTS_EL.includes(n)) return;
    let mirrors = this.sel.mirrors.filter((i) => i < n);
    /* In full-spiegel mode: keep every door mirrored when the count changes,
       so a 2→3 grow doesn't drop us out of spiegel-mode for the new door. */
    if (this.sel.panel === "spiegel-alles") {
      mirrors = Array.from({ length: n }, (_, i) => i);
    }
    this.sel    = { ...this.sel, count: n, mirrors };
    this._notice = null;
    this._maybeSwitchSpiegel();
    this._render();
  }

  _setFrame(id) {
    this.sel    = { ...this.sel, frame: id };
    this._notice = null;
    this._render();
  }

  _toggleMirror(i) {
    const mirrors = this.sel.mirrors.includes(i)
      ? this.sel.mirrors.filter((m) => m !== i)
      : [...this.sel.mirrors, i].sort((a, b) => a - b);
    this.sel    = { ...this.sel, mirrors };
    this._notice = null;
    this._maybeSwitchSpiegel();
    this._render();
  }

  /* Three-way spiegel auto-switch: swap the active "panel" so the cart line +
     URL + title always reflect the real product for the current mirror mix.
       - 0 mirrors        → the base decor (island.panels[i])
       - 1..count-1 mirrors → that decor's spiegel_product ("spiegel-decor:<id>")
       - all doors mirrored → the spiegel-schuifdeuren product ("spiegel-alles")
     Reverts to the previously active decor once every mirror is unchecked.
     opts.skipUrl: don't touch history (used on mount / deep-link seeding). */
  _maybeSwitchSpiegel(opts) {
    /* Paneel has no island.panels / spiegel_product concept — only omlijst
       switches products on mirror selection. No-op there (matches the old
       `if (!spiegel) return;` guard, which happened to always be true for
       paneel since island.spiegel_all is never emitted for that type). */
    if (this._island.type === "paneel" || !this._island.panels || !this._island.panels.length) return;
    const spiegel = this._spiegelAll;
    const sel = this.sel;
    const inSpiegelAlles = sel.panel === "spiegel-alles";
    const inSpiegelDecor = typeof sel.panel === "string" && sel.panel.startsWith("spiegel-decor:");
    const allMirrors = sel.count > 0 && sel.mirrors.length === sel.count;
    const anyMirrors = sel.mirrors.length > 0;
    const skipUrl = opts && opts.skipUrl;

    /* Resolve the "true" base panel we should be on if there were no mirrors. */
    let basePanel;
    if (inSpiegelAlles || inSpiegelDecor) {
      const prev = this._prevPanelBeforeSpiegel;
      basePanel = prev ? this._island.panels.find((p) => p.id === prev) : null;
      if (!basePanel) basePanel = this._island.panels && this._island.panels[0];
    } else {
      basePanel = this._island.panels.find((p) => p.id === sel.panel);
    }
    if (!basePanel) return;

    /* Target state: all → spiegel-alles; partial → spiegel-decor:<basePanelId>; none → basePanel.id */
    let targetPanelId;
    let targetProduct;
    if (allMirrors && spiegel) {
      targetPanelId = "spiegel-alles";
      targetProduct = spiegel;
    } else if (anyMirrors && basePanel.spiegel_product) {
      targetPanelId = "spiegel-decor:" + basePanel.id;
      targetProduct = basePanel.spiegel_product;
    } else {
      targetPanelId = basePanel.id;
      targetProduct = basePanel;
      /* Mirrors were requested but this decor can't honour them (no spiegel_product,
         and not a full set with a spiegel-alles product). Drop the mirror selection
         so the price shown and the variant added stay in sync — never show/charge a
         mirror we can't actually put in the cart. */
      if (anyMirrors) {
        this._prevPanelBeforeSpiegel = null;
        this.sel = { ...sel, mirrors: [], panel: targetPanelId };
        return;
      }
    }

    if (targetPanelId === sel.panel) return;

    /* Remember the base decor id so we can revert. */
    if (!inSpiegelAlles && !inSpiegelDecor && (allMirrors || anyMirrors)) {
      this._prevPanelBeforeSpiegel = basePanel.id;
    }
    if (!allMirrors && !anyMirrors) {
      this._prevPanelBeforeSpiegel = null;
    }

    this.sel = { ...sel, panel: targetPanelId };
    if (!skipUrl && targetProduct && targetProduct.product_url && typeof history !== "undefined" && history.replaceState) {
      const sep = targetProduct.product_url.indexOf("?") === -1 ? "?" : "&";
      try { history.replaceState(null, "", targetProduct.product_url + sep + this._cfgQueryString()); } catch (e) { /* noop */ }
    }
  }

  _toggleSoft() {
    this.sel    = { ...this.sel, soft: !this.sel.soft };
    this._notice = null;
    this._render();
  }

  _toggleMontage() {
    this.sel    = { ...this.sel, montage: !this.sel.montage };
    this._notice = null;
    this._render();
  }

  _setWall(hex) {
    if (!hex) return;
    this.sel = { ...this.sel, wall: hex };
    this._render();
  }

  /* ── gallery state ─────────────────────────────────── */
  _galleryStep(dir) {
    /* front view + the selected decor's product photos (profile view is hidden;
       keep in sync with the views[] list in _renderGalleryViewer). */
    const total = 1 + this._activeImages().length;
    this._view = ((this._view ?? 0) + dir + total) % total;
    this._renderGalleryOnly();
  }

  _gallerySetView(i) {
    this._view = i;
    this._renderGalleryOnly();
  }

  /** Re-render only the gallery subtree without rebuilding the entire element.
   *  This preserves input focus / scroll position in the options column. */
  _renderGalleryOnly() {
    const galleryEl = this.querySelector(".cfg-gallery");
    if (!galleryEl) { this._render(); return; }
    galleryEl.innerHTML = this._renderGalleryViewer();
  }

  /* ── add-to-cart ───────────────────────────────────── */

  /**
   * Pure helper — builds the cart `items` array from current sel + island.
   * Returns null if the matched door variant is unavailable or not found.
   * Safe to call in unit tests without DOM or fetch.
   *
   * @returns {{ items: Array<object> }|null}
   */
  _buildCartItems() {
    const island  = this._island;
    const sel     = this.sel;
    const addons  = island.addons || {};

    /* ── paneel branch ──────────────────────────────────────────────────── */
    if (island.type === "paneel") {
      const variant = cfgLogic.matchVariant(this._activeVariants(), {
        countLabel: sel.count + "-deurs",
        frameLabel: sel.frame,
      });
      if (!variant || !variant.available) return null;

      const activeKleur = this._activeKleur();
      const kleurLabel  = activeKleur ? activeKleur.label : (sel.kleur || "");
      const activeVlak  = island.vlakverdelingen && island.vlakverdelingen.find((v) => v.id === sel.vlak);
      const vlakLabel   = activeVlak ? activeVlak.label : (sel.vlak || "");
      const panelHex    = (activeKleur && activeKleur.hex) ? activeKleur.hex : "";

      const doorTitle   = this._activeTitle() || (kleurLabel + " paneeldeuren | op maat | 2 - 5 deurs");

      /* Spiegel property — only present when at least one mirror selected */
      const mirrorProp = sel.mirrors.length
        ? { Spiegel: "deur " + sel.mirrors.map((i) => i + 1).join(", ") }
        : {};

      const cfgString = this._cfgQueryString();
      const bundleId  = "cfg-" + Date.now() + "-" + Math.floor(Math.random() * 1e6);

      /* mirror toeslag child (paneel prices mirrors as a bundled line by priceGroup;
         omlijst bakes it into the variant instead — see the omlijst branch). */
      let mirrorId = 0;
      if (sel.mirrors.length) {
        const g = cfgLogic.priceGroup(variant.price, sel.count, island.pricegroup_threshold_cents);
        const m = (addons.mirror_by_group || {})[g];
        mirrorId = m ? parseInt(m.id, 10) : 0;
      }

      return cfgLogic.buildCartPayload({
        sel,
        variantId:  variant.id,
        parentProps: {
          Breedte:       sel.w,
          Hoogte:        sel.h,
          Paneelkleur:   kleurLabel,
          Profielkleur:  sel.frame,
          Vlakverdeling: vlakLabel,
          ...mirrorProp,
          Softclose:     sel.soft ? "Ja" : "Nee",
        },
        cfgString,
        bundleId,
        softId:      parseInt(addons.softclose, 10) || 0,
        montageId:   parseInt(addons.montage, 10) || 0,
        mirrorId,
        montageNote: "De schuifdeuren worden vakkundig gemonteerd door onze ervaren monteurs.",
      });
    }

    /* ── omlijst branch ────────────────────────────────────────────────── */
    /* 1. match door variant — on the SELECTED decor's product, so the cart line
       is the right product even though we never reloaded the page.
       spiegel-alles / base decor: no mirror-count suffix on the variant title.
       spiegel-decor (partial mirror): pass mirrorCount so matchVariant finds
       the "N-deurs met X spiegel / Frame" variant on that decor's mirror product. */
    const inSpiegelAlles = sel.panel === "spiegel-alles";
    const inSpiegelDecor = typeof sel.panel === "string" && sel.panel.startsWith("spiegel-decor:");
    const variant = cfgLogic.matchVariant(this._activeVariants(), {
      countLabel: sel.count + "-deurs",
      frameLabel: sel.frame,
      mirrorCount: inSpiegelDecor ? sel.mirrors.length : 0,
    });

    if (!variant || !variant.available) return null;

    /* panel label — spiegel-alles maps to the injected spiegel product, and
       spiegel-decor maps to the underlying base decor (the switched-to
       spiegel_product carries no .label of its own). Neither is a real
       island.panels entry, so island.panels.find() alone can't resolve them. */
    const activePanel = inSpiegelAlles && this._spiegelAll
      ? this._spiegelAll
      : inSpiegelDecor
        ? (island.panels.find((p) => p.id === sel.panel.slice("spiegel-decor:".length)) || island.panels[0])
        : (island.panels.find((p) => p.id === sel.panel) || island.panels[0]);
    const panelLabel  = activePanel ? activePanel.label : sel.panel;

    /* Spiegel property — only present when at least one mirror selected */
    const mirrorProp = sel.mirrors.length
      ? { Spiegel: "deur " + sel.mirrors.map((i) => i + 1).join(", ") }
      : {};

    /* Compact config string so "Aanpassen" can restore the exact selection. */
    const cfgString = this._cfgQueryString();
    const bundleId  = "cfg-" + Date.now() + "-" + Math.floor(Math.random() * 1e6);

    /* Omlijst bakes mirror cost into the matched variant (spiegel-decor /
       spiegel-alles product), so there is no mirror child line — mirrorId=0. */
    return cfgLogic.buildCartPayload({
      sel,
      variantId:  variant.id,
      parentProps: {
        Breedte:      sel.w,
        Hoogte:       sel.h,
        Paneelkleur:  panelLabel,
        Profielkleur: sel.frame,
        ...mirrorProp,
        Softclose:    sel.soft ? "Ja" : "Nee",
      },
      cfgString,
      bundleId,
      softId:      parseInt(addons.softclose, 10) || 0,
      montageId:   parseInt(addons.montage, 10) || 0,
      mirrorId:    0,
      montageNote: "De schuifdeuren worden vakkundig gemonteerd door onze ervaren monteurs.",
    });
  }

  /**
   * Thin wrapper: builds the cart payload and POSTs it to /cart/add.js.
   * On success, signals the theme cart drawer; falls back to /cart.
   * On unavailable variant: shows an inline error near the CTA.
   */
  async _addToCart() {
    /* clear any previous inline error */
    this._clearCartError();

    const payload = this._buildCartItems();

    if (!payload) {
      this._showCartError("Dit product is momenteel niet beschikbaar voor de geselecteerde configuratie.");
      return;
    }

    /* Montage is a one-off, order-level service: if one is already in the cart,
       don't add a second (even across multiple door configurations). */
    const montageId = parseInt((this._island.addons || {}).montage, 10);
    if (montageId && payload.items.some((it) => it.id === montageId)) {
      try {
        const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
        const cart = await cartRes.json();
        if (cart.items && cart.items.some((li) => li.id === montageId || li.variant_id === montageId)) {
          payload.items = payload.items.filter((it) => it.id !== montageId);
        }
      } catch (e) { /* if the check fails, fall through and add normally */ }
    }

    /* disable CTA while request is in flight */
    const ctaBtn = this.querySelector("[data-action='add-to-cart']");
    if (ctaBtn) ctaBtn.disabled = true;

    try {
      const res = await fetch("/cart/add.js", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const msg = (errData && errData.description) || "Er is een fout opgetreden. Probeer het opnieuw.";
        this._showCartError(msg);
        return;
      }

      /* "Aanpassen": the re-add succeeded, so NOW clear the bundle we were editing.
         Doing this after the add (never before) means an abandoned edit keeps the
         original order intact. The freshly-added bundle has a new id, so it stays. */
      if (this._replaceBundleId) {
        try {
          const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
          const cart = await cartRes.json();
          const updates = {};
          (cart.items || []).forEach((li) => {
            if (li.properties && li.properties._bundle_id === this._replaceBundleId) updates[li.key] = 0;
          });
          if (Object.keys(updates).length) {
            await fetch("/cart/update.js", {
              method:  "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body:    JSON.stringify({ updates }),
            });
          }
        } catch (e) { /* worst case is a leftover duplicate line, never a lost order */ }
        this._replaceBundleId = null;
      }

      /* success — open theme cart drawer */
      /* 1. standard Shopify theme event */
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      /* 2. common alternatives used by various themes */
      window.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("theme:cart:open", { bubbles: true }));
      /* 3. Dawn / Refresh theme helper */
      if (typeof window.Shopify !== "undefined" && typeof window.Shopify.onCartUpdate === "function") {
        window.Shopify.onCartUpdate({});
      }
      /* 4. robust fallback — navigate to /cart */
      window.location.href = "/cart";

    } catch (err) {
      this._showCartError("Netwerkfout — controleer je verbinding en probeer het opnieuw.");
    } finally {
      if (ctaBtn) ctaBtn.disabled = false;
    }
  }

  /** Render an inline error message near the CTA row. */
  _showCartError(message) {
    this._clearCartError();
    const ctaRow = this.querySelector(".cfg-cta-row");
    if (!ctaRow) return;
    const el = document.createElement("p");
    el.className = "cfg-cart-error";
    el.setAttribute("role", "alert");
    el.textContent = message;
    ctaRow.insertAdjacentElement("afterend", el);
  }

  /** Remove any existing inline cart error. */
  _clearCartError() {
    const existing = this.querySelector(".cfg-cart-error");
    if (existing) existing.remove();
  }
}

customElements.define("door-configurator", DoorConfigurator);

} /* end browser guard */
